import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  Typography,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  setDoc,
  getDoc,
  writeBatch,
  deleteDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { storage } from '../firebase';
import BankTransactionDrawer from '../components/BankTransactionDrawer';
import { computeGstAmountForUnit } from '../office/gst';
import { applyTemplateTokens } from '../office/templateTokens';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { ensureTemplateSequenceRefNo, normalizeOfficeRefPrefix } from '../office/documentRefs';
import { compareUnitNumbers } from '../utils/unitSort';
import { usePaymentDetail } from '../contexts/PaymentDetailContext';
import { useAddPayment }    from '../contexts/AddPaymentContext';

const WHATSAPP_HEADER_PROJECT_LOGO_TOKEN = '{{project_logo_url}}';
const WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN = '{{voucher_or_project_logo_url}}';

const resolveProjectLogoForTemplate = (projectRow: any, templateRow: any): string => {
  const prefersBw = Boolean(templateRow?.useProjectLogoBw ?? templateRow?.visualModel?.useProjectLogoBw);
  const bwLogo = String(projectRow?.logoBwUrl || '').trim();
  const colorLogo = String(projectRow?.logoUrl || '').trim();
  if (prefersBw && bwLogo) return bwLogo;
  return colorLogo;
};

const resolveCompanyLogoForTemplate = (companyRow: any, templateRow: any): string => {
  const prefersBw = Boolean(templateRow?.useProjectLogoBw ?? templateRow?.visualModel?.useProjectLogoBw);
  const bwLogo = String(companyRow?.logoBwUrl || '').trim();
  const colorLogo = String(companyRow?.logoUrl || '').trim();
  if (prefersBw && bwLogo) return bwLogo;
  return colorLogo;
};

type TxnType = 'receipt' | 'payment';

type BankAccount = {
  id: string;
  name?: string | null;
  bankName?: string | null;
  accountLast4?: string | null;
  holderName?: string | null;
  accountRole?: string | null;
  allowCustomerReceipts?: boolean | null;
  allowGstReceipts?: boolean | null;
  allowPayments?: boolean | null;
  restrictedOutflow?: boolean | null;
};

type BankTransaction = {
  id: string;
  bankAccountId?: string | null;
  txnType?: TxnType | null;
  receiptCategory?: 'customer' | 'gst' | 'advocate' | 'other' | null;
  isLoanDisbursement?: boolean | null;
  paymentCategory?: 'customer_refund' | string | null;
  legalChargeType?: 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee' | null;
  unitId?: string | null;
  unitNumber?: string | null;
  bookingId?: string | null;
  amount?: number | null;
  txnDate?: any;
  txnDateYMD?: string | null;
  legacyChqDate?: string | null;
  legacyTransactionDate?: string | null;
  importSource?: string | null;
  mode?: string | null;
  referenceNo?: string | null;
  partyName?: string | null;
  payerBankName?: string | null;
  notes?: string | null;
  voucherUrl?: string | null;
  voucherName?: string | null;
  voucherPath?: string | null;
  voucherUploadedAt?: any;
  voucherUploadedBy?: string | null;
  createdAt?: any;
  createdBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
  isReconciled?: boolean | null;
  reconciliationRangeKey?: string | null;
  reconciledAt?: any;
  reconciledBy?: string | null;
  fundTransferProcessed?: boolean | null;
  fundTransferProcessedAt?: any;
  fundTransferRequestId?: string | null;
  refundAgainstTxnId?: string | null;
  refundAgainstTxnIds?: string[] | null;
  refundAllocations?: Array<{
    txnId?: string | null;
    amount?: number | null;
    receiptCategory?: 'customer' | 'gst' | string | null;
  }> | null;
};

const refundNeedsReceiptLink = (transaction: BankTransaction): boolean => {
  const isCustomerRefund =
    String(transaction?.txnType || '').toLowerCase() === 'payment' &&
    String(transaction?.paymentCategory || '').toLowerCase() === 'customer_refund';
  if (!isCustomerRefund) return false;

  const allocations = Array.isArray(transaction?.refundAllocations)
    ? transaction.refundAllocations
    : [];
  return !allocations.some((allocation) =>
    String(allocation?.txnId || '').trim() && Number(allocation?.amount || 0) > 0
  ) && !String(transaction?.refundAgainstTxnId || '').trim();
};

type ReconciliationDoc = {
  id: string;
  bankAccountId?: string | null;
  rangeEndDate?: string | null;
  statementClosingBalance?: number | null;
  finalized?: boolean | null;
  reconciledAt?: any;
  updatedAt?: any;
};

type TransferRequestDoc = {
  id: string;
  status?: 'draft' | 'generated' | 'emailed' | string;
  selectedReceiptIds?: string[];
  selectedReceiptTotal?: number;
  transferRequestAmount?: number;
  projectReceiptCumulativeTillDate?: number;
  transferStartDate?: string;
  openingCumulativeReceipt?: number;
  refNo?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  generatedAt?: any;
  emailedAt?: any;
  file?: {
    name?: string;
    originalName?: string;
    url?: string;
    path?: string;
    uploadedAtMs?: number;
    uploadedBy?: string;
  } | null;
};

type TransferEmailPreview = {
  templateName: string;
  to: string[];
  subject: string;
  bodyHtml: string;
  attachments: Array<{ key: string; name: string; source: string }>;
};

type RefundReceiptOption = {
  id: string;
  unitId: string;
  unitNumber: string;
  bookingId: string;
  txnDate: Date | null;
  dateLabel: string;
  amount: number;
  alreadyRefunded: number;
  refundableBalance: number;
  receiptCategory: 'customer' | 'gst';
  partyName: string;
  referenceNo: string;
};

const isReconciliationDone = (row: ReconciliationDoc | null | undefined) =>
  Boolean((row as any)?.finalized || (row as any)?.reconciledAt);

const toMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
  if (typeof value?.seconds === 'number') return Number(value.seconds || 0) * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};


function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function formatDDMMYYYY(date: Date) {
  return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
}

function tsToDate(ts: any): Date | null {
  try {
    if (!ts) return null;
    if (typeof ts?.toDate === 'function') return ts.toDate();
    if (typeof ts === 'number') return new Date(ts);
    if (ts instanceof Date) return ts;
    return null;
  } catch {
    return null;
  }
}

function ymdToday() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function dateFromYMD(ymd: string): Date | null {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const d = new Date(y, mo, da);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function ceilHundred(value: number) {
  return Math.ceil(value / 100) * 100;
}

function formatAmount2(value: number): string {
  const safe = Number(value || 0);
  return safe.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function normalizeKey(value: any) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeSearchValue(value: any): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[₹,]/g, '')
    .replace(/\s+/g, ' ');
}

function normalizeSearchCompact(value: any): string {
  return normalizeSearchValue(value).replace(/[^a-z0-9.]/g, '');
}

function parseLooseNumber(value: any): number {
  const cleaned = String(value ?? '')
    .replace(/[₹,\s]/g, '')
    .trim();
  if (!cleaned) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function parseLooseDate(value: any): Date | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

type DuplicateMatchKind = 'exact' | 'likely';

type DuplicateMatch = {
  kind: DuplicateMatchKind;
  row: BankTransaction;
  dateText: string;
  dayDiff: number;
};

function normalizeDupText(value: any): string {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeDupRef(value: any): string {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function coerceTxnDateForDuplicate(row: any): Date | null {
  return (
    tsToDate(row?.txnDate) ||
    parseLooseDate(String(row?.legacyChqDate || row?.legacyTransactionDate || '').trim()) ||
    null
  );
}

function bankReceiptDisplayDate(row: any): Date | null {
  return (
    parseLooseDate(String(row?.legacyChqDate || '').trim()) ||
    parseLooseDate(String(row?.txnDateYMD || '').trim()) ||
    tsToDate(row?.txnDate) ||
    parseLooseDate(String(row?.legacyTransactionDate || '').trim()) ||
    tsToDate(row?.createdAt) ||
    null
  );
}

function dayDiffAbs(a: Date, b: Date): number {
  const dayMs = 24 * 60 * 60 * 1000;
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.abs(Math.round((ua - ub) / dayMs));
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      row.push(field);
      field = '';
      continue;
    }
    if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      const hasContent = row.some((c) => String(c || '').trim() !== '');
      if (hasContent) rows.push(row);
      row = [];
      continue;
    }
    field += ch;
  }
  row.push(field);
  if (row.some((c) => String(c || '').trim() !== '')) rows.push(row);
  return rows;
}

export default function OfficeBanksPage() {
  const { currentUser, currentProjectId, roleId } = useAuth();
  const db = useMemo(() => getFirestore(), []);
  const isAdmin = String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { openPaymentDetail } = usePaymentDetail();
  const { openAddPayment }    = useAddPayment();

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [unitOptions, setUnitOptions] = useState<Array<{
    id: string;
    unitNumber: string;
    blockType?: string | null;
    usage?: string | null;
    activeBookingId?: string | null;
    bookingStatus?: string | null;
    unitAmount?: number | null;
    stampDuty?: number | null;
    registrationCharges?: number | null;
    loanToDeveloperAmount?: number | null;
  }>>([]);
  const [txns, setTxns] = useState<BankTransaction[]>([]);
  const [reconciliations, setReconciliations] = useState<ReconciliationDoc[]>([]);
  const [projectAdvocateAgreementFee, setProjectAdvocateAgreementFee] = useState<number>(0);
  const [projectAdvocateDeedFee, setProjectAdvocateDeedFee] = useState<number>(0);
  const [selectedUnitCustomers, setSelectedUnitCustomers] = useState<any[]>([]);
  const [projectName, setProjectName] = useState<string>('');
  const [projectLogoUrl, setProjectLogoUrl] = useState<string>('');
  const [projectBackgroundImageUrl, setProjectBackgroundImageUrl] = useState<string>('');
  const [projectGstPercentByUnitType, setProjectGstPercentByUnitType] = useState<Record<string, number>>({});
  const [projectDefaultGstPercent, setProjectDefaultGstPercent] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<TxnType | 'all'>('all');
  const [reconcileFilter, setReconcileFilter] = useState<'all' | 'reconciled' | 'unreconciled'>('all');
  const [filterBankId, setFilterBankId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'transactionDate' | 'chqDate' | 'amount'>('chqDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const drawerWidth = 520;
  const panelGap = 12;
  const [detailOffset, setDetailOffset] = useState(0);
  const [txnDrawerOpen, setTxnDrawerOpen] = useState(false);
  const [txnDetailOpen, setTxnDetailOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState<BankTransaction | null>(null);
  const [manualReconciling, setManualReconciling] = useState(false);
  const [editingTxn, setEditingTxn] = useState<BankTransaction | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingTxn, setDeletingTxn] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lockedView, setLockedView] = useState(false);
  const [editableFields, setEditableFields] = useState<{
    bank: boolean;
    amount: boolean;
    referenceNo: boolean;
    txnType: boolean;
    receiptCategory: boolean;
  }>({
    bank: false,
    amount: false,
    referenceNo: false,
    txnType: false,
    receiptCategory: false,
  });

  // Form fields
  const [txnType, setTxnType] = useState<TxnType>('receipt');
  const [receiptCategory, setReceiptCategory] = useState<'customer' | 'gst' | 'advocate' | 'other'>('customer');
  const [isLoanDisbursement, setIsLoanDisbursement] = useState(false);
  const [paymentCategory, setPaymentCategory] = useState<string>('');
  const [legalChargeType, setLegalChargeType] = useState<
    '' | 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee'
  >('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [txnDateYMD, setTxnDateYMD] = useState(ymdToday());
  const [amount, setAmount] = useState('');
  const [partyName, setPartyName] = useState('');
  const [payerBankName, setPayerBankName] = useState('');
  const [payerBankAutofillSuppressed, setPayerBankAutofillSuppressed] = useState(false);
  const [unitId, setUnitId] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [mode, setMode] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [notes, setNotes] = useState('');
  const [refundAgainstReceiptIds, setRefundAgainstReceiptIds] = useState<string[]>([]);
  const [voucherFile, setVoucherFile] = useState<File | null>(null);
  const [voucherUploading, setVoucherUploading] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([]);
  const [duplicatePendingSendAfterSave, setDuplicatePendingSendAfterSave] = useState(false);
  const [duplicateProceeding, setDuplicateProceeding] = useState(false);
  const [liveDuplicateMatches, setLiveDuplicateMatches] = useState<DuplicateMatch[]>([]);
  const [importingCsv, setImportingCsv] = useState(false);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [bankActionsAnchorEl, setBankActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingImport, setPendingImport] = useState<{
    fileName: string;
    rows: string[][];
    headerIndex: Record<string, number>;
    sourceAccounts: string[];
  } | null>(null);
  const [pendingAccountMapping, setPendingAccountMapping] = useState<Record<string, string>>({});
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waTemplates, setWaTemplates] = useState<any[]>([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplatesError, setWaTemplatesError] = useState<string | null>(null);
  const [waSenders, setWaSenders] = useState<any[]>([]);
  const [waSenderId, setWaSenderId] = useState<string>('');
  const [waTemplateId, setWaTemplateId] = useState<string>('');
  const [waTo, setWaTo] = useState<string>('');
  const [waSendError, setWaSendError] = useState<string | null>(null);
  const [waSendSuccess, setWaSendSuccess] = useState<string | null>(null);
  const [waSending, setWaSending] = useState(false);
  const voucherInputRef = useRef<HTMLInputElement | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const [transferRequests, setTransferRequests] = useState<TransferRequestDoc[]>([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferSelectedReceiptIds, setTransferSelectedReceiptIds] = useState<string[]>([]);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferGenerating, setTransferGenerating] = useState(false);
  const [transferSending, setTransferSending] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);
  const [transferGeneratedRequest, setTransferGeneratedRequest] = useState<TransferRequestDoc | null>(null);
  const [projectDocumentTemplates, setProjectDocumentTemplates] = useState<any[]>([]);
  const [projectEmailTemplates, setProjectEmailTemplates] = useState<any[]>([]);
  const [transferEmailPreviewOpen, setTransferEmailPreviewOpen] = useState(false);
  const [transferEmailPreviewLoading, setTransferEmailPreviewLoading] = useState(false);
  const [transferEmailPreviewError, setTransferEmailPreviewError] = useState<string | null>(null);
  const [transferEmailPreviewData, setTransferEmailPreviewData] = useState<TransferEmailPreview | null>(null);
  const [transferEmailToDraft, setTransferEmailToDraft] = useState('');
  const [transferHistoryDialogOpen, setTransferHistoryDialogOpen] = useState(false);
  const FUND_TRANSFER_DOC_TEMPLATE_ID = 'fund_transfer_request_v1';
  const FUND_TRANSFER_EMAIL_TEMPLATE_KEY = 'fund_transfer_request';

  const paymentModeOptions = useMemo(
    () => [
      'Cash',
      'Cheque',
      'NEFT/RTGS',
      'UPI',
      'IMPS',
      'Card',
      'Net Banking',
      'PG/Online',
      'Other',
    ],
    []
  );

  const partyNameOptions = useMemo(() => {
    const set = new Set<string>();
    txns.forEach((t) => {
      const v = String(t.partyName || '').trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [txns]);

  const payerBankNameOptions = useMemo(() => {
    const set = new Set<string>();
    txns.forEach((t) => {
      const v = String((t as any).payerBankName || '').trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [txns]);

  const isCustomerRefundMode =
    txnType === 'payment' && String(paymentCategory || '').trim().toLowerCase() === 'customer_refund';

  useEffect(() => {
    setDetailOffset(!isMobile && (txnDrawerOpen || txnDetailOpen) ? drawerWidth + panelGap : 0);
  }, [isMobile, txnDetailOpen, txnDrawerOpen]);

  useEffect(() => {
    setLoadError(null);
    setLoading(true);
    setTxns([]);
    setBankAccounts([]);
    setReconciliations([]);
    setUnitOptions([]);
    if (!currentProjectId) {
      setLoading(false);
      return undefined;
    }

    const unsubBanks = onSnapshot(
      query(collection(db, 'bankAccounts'), orderBy('name', 'asc'), limit(500)),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BankAccount[];
        const pid = String(currentProjectId || '');
        const next = all.filter((b: any) =>
          Array.isArray(b.projectIds) && b.projectIds.map((x: any) => String(x)).includes(pid)
        );
        setBankAccounts(next);
      },
      (err) => {
        console.error('Load bank accounts failed', err);
        setBankAccounts([]);
      }
    );

    const unsubUnits = onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'units'), orderBy('unitNumberLower', 'asc'), limit(1000)),
      (snap) => {
        const next = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((u) => u.isActive !== false)
          .map((u) => ({
            id: u.id,
            unitNumber: String(u.unitNumber || u.id),
            blockType: String(u.blockType || u.unitType || u.type || '').trim() || null,
            usage: String(u.usage || '').trim() || null,
            activeBookingId: u.activeBookingId || null,
            bookingStatus: u.bookingStatus || null,
            unitAmount: typeof u.unitAmount === 'number' ? u.unitAmount : null,
            stampDuty:
              typeof u.stampDuty === 'number'
                ? u.stampDuty
                : typeof u.stamp_duty === 'number'
                ? u.stamp_duty
                : null,
            registrationCharges:
              typeof u.registrationCharges === 'number'
                ? u.registrationCharges
                : typeof u.registration_charges === 'number'
                ? u.registration_charges
                : null,
            loanToDeveloperAmount: Math.max(0, Number(u.loanToDeveloperAmount || 0) || 0),
          }))
          .sort((a, b) => compareUnitNumbers(a.unitNumber || a.id, b.unitNumber || b.id));
        setUnitOptions(next);
      },
      (err) => {
        console.error('Load units failed', err);
        setUnitOptions([]);
      }
    );

    const txnsByTxnDate = new Map<string, BankTransaction>();
    const txnsByCreatedAt = new Map<string, BankTransaction>();
    const publishTxns = () => {
      const merged = new Map<string, BankTransaction>();
      txnsByTxnDate.forEach((row, id) => merged.set(id, row));
      txnsByCreatedAt.forEach((row, id) => merged.set(id, row));
      setTxns(Array.from(merged.values()));
      setLoading(false);
    };

    const unsubTxnsByTxnDate = onSnapshot(
      query(
        collection(db, 'projects', currentProjectId, 'bankTransactions'),
        orderBy('txnDate', 'desc'),
        limit(1000)
      ),
      (snap) => {
        txnsByTxnDate.clear();
        snap.docs.forEach((d) => txnsByTxnDate.set(d.id, { id: d.id, ...(d.data() as any) } as BankTransaction));
        publishTxns();
      },
      (err) => {
        console.error('Load bank transactions failed', err);
        setLoadError(err?.message || 'Failed to load bank transactions.');
        setLoading(false);
      }
    );
    const unsubTxnsByCreatedAt = onSnapshot(
      query(
        collection(db, 'projects', currentProjectId, 'bankTransactions'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      ),
      (snap) => {
        txnsByCreatedAt.clear();
        snap.docs.forEach((d) => txnsByCreatedAt.set(d.id, { id: d.id, ...(d.data() as any) } as BankTransaction));
        publishTxns();
      },
      (err) => {
        console.error('Load recent bank transactions failed', err);
        publishTxns();
      }
    );

    const unsubRecon = onSnapshot(
      query(
        collection(db, 'projects', currentProjectId, 'bankReconciliations'),
        orderBy('updatedAt', 'desc'),
        limit(1000)
      ),
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as ReconciliationDoc[];
        setReconciliations(next);
      },
      () => setReconciliations([])
    );

    let cancelled = false;
    (async () => {
      try {
        const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
        if (cancelled) return;
        if (projectSnap.exists()) {
          const row: any = projectSnap.data() || {};
          setProjectName(String(row?.name || ''));
          setProjectLogoUrl(String(row?.logoUrl || '').trim());
          setProjectBackgroundImageUrl(String(row?.backgroundImageUrl || '').trim());
          setProjectAdvocateAgreementFee(Number(row?.advocateAgreementFee || 0) || 0);
          setProjectAdvocateDeedFee(Number(row?.advocateDeedFee || 0) || 0);
          const rawMap = ({
            ...(row?.gstPercentByUnitType || {}),
            ...(row?.gstPercentByUsage || {}),
          }) as Record<string, any>;
          const nextMap: Record<string, number> = {};
          Object.keys(rawMap || {}).forEach((k) => {
            const key = String(k || '').trim();
            const value = Number(rawMap[k]);
            if (!key || !Number.isFinite(value) || value < 0) return;
            nextMap[key] = value;
          });
          setProjectGstPercentByUnitType(nextMap);
          const defaultPercent = Number(row?.gstDefaultPercent ?? 1);
          setProjectDefaultGstPercent(Number.isFinite(defaultPercent) && defaultPercent >= 0 ? defaultPercent : 1);
        } else {
          setProjectName('');
          setProjectLogoUrl('');
          setProjectBackgroundImageUrl('');
          setProjectAdvocateAgreementFee(0);
          setProjectAdvocateDeedFee(0);
          setProjectGstPercentByUnitType({});
          setProjectDefaultGstPercent(1);
        }
      } catch {
        if (!cancelled) {
          setProjectName('');
          setProjectLogoUrl('');
          setProjectBackgroundImageUrl('');
          setProjectAdvocateAgreementFee(0);
          setProjectAdvocateDeedFee(0);
          setProjectGstPercentByUnitType({});
          setProjectDefaultGstPercent(1);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubBanks();
      unsubUnits();
      unsubTxnsByTxnDate();
      unsubTxnsByCreatedAt();
      unsubRecon();
    };
  }, [currentProjectId, db]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) {
      setWaTemplates([]);
      setWaSenders([]);
      return;
    }
    setWaTemplatesLoading(true);
    setWaTemplatesError(null);
    const unsubTemplates = onSnapshot(
      query(collection(db, 'whatsappTemplates'), orderBy('updatedAt', 'desc')),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setWaTemplates(rows);
        setWaTemplatesLoading(false);
      },
      (err) => {
        console.error('Load WhatsApp templates failed', err);
        setWaTemplatesError(err?.message || 'Failed to load WhatsApp templates.');
        setWaTemplatesLoading(false);
      }
    );
    const unsubSenders = onSnapshot(
      collection(db, 'whatsappSenders'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')));
        setWaSenders(rows);
      },
      () => setWaSenders([])
    );
    return () => {
      unsubTemplates();
      unsubSenders();
    };
  }, [currentProjectId, db, isAdmin]);

  useEffect(() => {
    if (!currentProjectId) {
      setTransferRequests([]);
      return;
    }
    const unsub = onSnapshot(
      query(
        collection(db, 'projects', currentProjectId, 'transferRequests'),
        orderBy('createdAt', 'desc'),
        limit(200)
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as TransferRequestDoc[];
        setTransferRequests(rows);
      },
      () => setTransferRequests([])
    );
    return () => unsub();
  }, [currentProjectId, db]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) {
      setProjectDocumentTemplates([]);
      setProjectEmailTemplates([]);
      return;
    }
    const unsubDocs = onSnapshot(
      query(collection(db, 'officeDocumentTemplates'), orderBy('updatedAt', 'desc'), limit(1000)),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => {
            const assigned = Array.isArray(row?.assignedProjectIds) ? row.assignedProjectIds.map((x: any) => String(x)) : [];
            return assigned.length === 0 || assigned.includes(String(currentProjectId));
          });
        setProjectDocumentTemplates(rows);
      },
      () => setProjectDocumentTemplates([])
    );
    const unsubEmails = onSnapshot(
      query(collection(db, 'officeEmailTemplates'), orderBy('updatedAt', 'desc'), limit(1000)),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => row?.active !== false)
          .filter((row: any) => {
            const assigned = Array.isArray(row?.assignedProjectIds) ? row.assignedProjectIds.map((x: any) => String(x)) : [];
            return assigned.length === 0 || assigned.includes(String(currentProjectId));
          });
        setProjectEmailTemplates(rows);
      },
      () => setProjectEmailTemplates([])
    );
    return () => {
      unsubDocs();
      unsubEmails();
    };
  }, [currentProjectId, db, isAdmin]);

  const bankById = useMemo(() => {
    const m = new Map<string, BankAccount>();
    bankAccounts.forEach((b) => m.set(b.id, b));
    return m;
  }, [bankAccounts]);
  const isCollectionBankById = useMemo(() => {
    const map = new Map<string, boolean>();
    bankAccounts.forEach((bank) => {
      const roleRaw = String(bank.accountRole || '').toLowerCase();
      const allowCustomer =
        typeof bank.allowCustomerReceipts === 'boolean'
          ? !!bank.allowCustomerReceipts
          : roleRaw === 'collection';
      map.set(String(bank.id || ''), allowCustomer);
    });
    return map;
  }, [bankAccounts]);
  const showTransferStatusColumn = useMemo(() => {
    const selectedId = String(filterBankId || '').trim();
    if (!selectedId) return false;
    return isCollectionBankById.get(selectedId) === true;
  }, [filterBankId, isCollectionBankById]);

  const bankHeaderCards = useMemo(() => {
    return bankAccounts
      .slice()
      .sort((left, right) =>
        String(right.name || right.bankName || right.id).localeCompare(
          String(left.name || left.bankName || left.id)
        )
      )
      .map((bank) => {
      const bankId = String(bank.id || '').trim();
      const currentBalance = txns
        .filter((txn) => String(txn.bankAccountId || '').trim() === bankId)
        .reduce((sum, txn) => {
          const amount = Number(txn.amount || 0);
          const isPayment = String(txn.txnType || '').toLowerCase() === 'payment';
          return sum + (isPayment ? -amount : amount);
        }, 0);
      const latestReconciled =
        reconciliations
          .filter((row) => String(row.bankAccountId || '').trim() === bankId)
          .filter((row) => isReconciliationDone(row))
          .slice()
          .sort((a, b) => {
            const ae = Date.parse(`${String(a.rangeEndDate || '')}T00:00:00`) || 0;
            const be = Date.parse(`${String(b.rangeEndDate || '')}T00:00:00`) || 0;
            if (be !== ae) return be - ae;
            return toMs((b as any)?.updatedAt) - toMs((a as any)?.updatedAt);
          })[0] || null;
      return {
        id: bankId,
        name: String(bank.name || bank.bankName || bankId),
        currentBalance,
        reconciledBalance: Number((latestReconciled as any)?.statementClosingBalance || 0),
        reconciledDate: String((latestReconciled as any)?.rangeEndDate || ''),
      };
    });
  }, [bankAccounts, reconciliations, txns]);

  const txnIsUnreconciled = (txn: BankTransaction) => {
    const source = String((txn as any)?.importSource || '').trim();
    if (source === 'legacy_csv') {
      const rawTxnDate = String((txn as any)?.legacyTransactionDate || '').trim();
      if (parseLooseDate(rawTxnDate)) return false;
      const legacyTxnDateObj = tsToDate(txn.txnDate);
      if (legacyTxnDateObj) return false;
      if (typeof txn.isReconciled === 'boolean') return !txn.isReconciled;
      return true;
    }
    const reconciliationKey = String((txn as any)?.reconciliationRangeKey || '').trim();
    if (reconciliationKey) return false;
    if (typeof txn.isReconciled === 'boolean') return !txn.isReconciled;
    return true;
  };
  const bankIdByAnyName = useMemo(() => {
    const m = new Map<string, string>();
    bankAccounts.forEach((b) => {
      const id = String(b.id || '').trim();
      if (!id) return;
      const options = [b.id, b.name, b.bankName].map((x) => normalizeKey(x)).filter(Boolean);
      options.forEach((key) => {
        if (!m.has(key)) m.set(key, id);
      });
    });
    return m;
  }, [bankAccounts]);
  const unitByNumber = useMemo(() => {
    const m = new Map<
      string,
      { id: string; unitNumber: string; activeBookingId?: string | null }
    >();
    unitOptions.forEach((u) => {
      const key = normalizeKey(u.unitNumber);
      if (key && !m.has(key)) {
        m.set(key, {
          id: u.id,
          unitNumber: u.unitNumber,
          activeBookingId: u.activeBookingId || null,
        });
      }
    });
    return m;
  }, [unitOptions]);

  const customerReceiptTotals = useMemo(() => {
    const byUnitId = new Map<string, number>();
    const byUnitNumber = new Map<string, number>();
    txns.forEach((t) => {
      if ((t.txnType || 'receipt') !== 'receipt') return;
      if ((t.receiptCategory || 'customer') !== 'customer') return;
      if (t.isLoanDisbursement === true) return;
      const amt = typeof t.amount === 'number' ? t.amount : 0;
      const uid = String(t.unitId || '').trim();
      const uno = String(t.unitNumber || '').trim();
      if (uid) byUnitId.set(uid, (byUnitId.get(uid) || 0) + amt);
      if (uno) byUnitNumber.set(uno, (byUnitNumber.get(uno) || 0) + amt);
    });
    return { byUnitId, byUnitNumber };
  }, [txns]);

  const gstReceiptTotals = useMemo(() => {
    const byUnitId = new Map<string, number>();
    const byUnitNumber = new Map<string, number>();
    txns.forEach((t) => {
      if ((t.txnType || 'receipt') !== 'receipt') return;
      if ((t.receiptCategory || '') !== 'gst') return;
      const amt = typeof t.amount === 'number' ? t.amount : 0;
      const uid = String(t.unitId || '').trim();
      const uno = String(t.unitNumber || '').trim();
      if (uid) byUnitId.set(uid, (byUnitId.get(uid) || 0) + amt);
      if (uno) byUnitNumber.set(uno, (byUnitNumber.get(uno) || 0) + amt);
    });
    return { byUnitId, byUnitNumber };
  }, [txns]);

  const eligibleUnitOptions = useMemo(() => {
    const isCustomer = txnType === 'receipt' && receiptCategory === 'customer';
    const isGst = txnType === 'receipt' && receiptCategory === 'gst';
    const isAdvocate = txnType === 'receipt' && receiptCategory === 'advocate';
    if (!isCustomer && !isGst && !isAdvocate) return unitOptions;
    return unitOptions.filter((u) => {
      const status = String(u.bookingStatus || '').toLowerCase();
      // Only show units that are booked/sold (not available/blocked).
      if (status && status !== 'booked' && status !== 'sold') return false;
      if (!status && !u.activeBookingId) return false;
      if (isAdvocate) return true;
      const expected = typeof u.unitAmount === 'number' ? u.unitAmount : null;
      if (expected == null) return true;
      if (isGst) {
        const gstExpected = computeGstAmountForUnit(
          expected,
          u as any,
          {
            gstPercentByUnitType: projectGstPercentByUnitType,
            gstDefaultPercent: projectDefaultGstPercent,
          },
          1
        );
        const gstPaid =
          (u.id ? gstReceiptTotals.byUnitId.get(u.id) : 0) ||
          (u.unitNumber ? gstReceiptTotals.byUnitNumber.get(u.unitNumber) : 0) ||
          0;
        return gstPaid < gstExpected;
      }
      const paid =
        (u.id ? customerReceiptTotals.byUnitId.get(u.id) : 0) ||
        (u.unitNumber ? customerReceiptTotals.byUnitNumber.get(u.unitNumber) : 0) ||
        0;
      const dpExpected = Math.max(0, expected - Number(u.loanToDeveloperAmount || 0));
      return paid < dpExpected;
    });
  }, [
    customerReceiptTotals,
    gstReceiptTotals,
    projectDefaultGstPercent,
    projectGstPercentByUnitType,
    receiptCategory,
    txnType,
    unitOptions,
  ]);

  const selectedUnitForReceipt = useMemo(
    () =>
      (unitId ? unitOptions.find((u) => u.id === unitId) : null) ||
      (unitNumber ? unitOptions.find((u) => u.unitNumber === unitNumber) : null) ||
      null,
    [unitId, unitNumber, unitOptions]
  );
  const selectedLoanToDeveloperAmount = Math.max(0, Number(selectedUnitForReceipt?.loanToDeveloperAmount || 0) || 0);
  const showLoanDisbursementOption =
    txnType === 'receipt' && receiptCategory === 'customer' && selectedLoanToDeveloperAmount > 0;

  useEffect(() => {
    if (!showLoanDisbursementOption) setIsLoanDisbursement(false);
  }, [showLoanDisbursementOption]);

  const refundAllocatedByReceiptId = useMemo(() => {
    const map = new Map<string, number>();
    const editingId = String(editingTxn?.id || '').trim();
    txns.forEach((txn) => {
      const isRefund =
        String(txn?.txnType || '').toLowerCase() === 'payment' &&
        String((txn as any)?.paymentCategory || '').toLowerCase() === 'customer_refund';
      if (!isRefund) return;
      const txnId = String(txn?.id || '').trim();
      if (editingId && txnId === editingId) return;
      const allocations = Array.isArray((txn as any)?.refundAllocations) ? (txn as any).refundAllocations : [];
      if (allocations.length) {
        allocations.forEach((row: any) => {
          const againstId = String(row?.txnId || '').trim();
          const value = Number(row?.amount || 0);
          if (!againstId || !(value > 0)) return;
          map.set(againstId, (map.get(againstId) || 0) + value);
        });
        return;
      }
      const fallbackAgainstId = String((txn as any)?.refundAgainstTxnId || '').trim();
      const fallbackAmount = Number(txn?.amount || 0);
      if (fallbackAgainstId && fallbackAmount > 0) {
        map.set(fallbackAgainstId, (map.get(fallbackAgainstId) || 0) + fallbackAmount);
      }
    });
    return map;
  }, [editingTxn?.id, txns]);

  const refundUnitOptions = useMemo(() => {
    const unitHasReceipts = new Set<string>();
    txns.forEach((txn) => {
      if (String(txn?.txnType || '').toLowerCase() !== 'receipt') return;
      const receiptKind = String(txn?.receiptCategory || '').toLowerCase();
      if (receiptKind !== 'customer' && receiptKind !== 'gst') return;
      const uid = String(txn?.unitId || '').trim();
      const uno = String(txn?.unitNumber || '').trim();
      if (uid) unitHasReceipts.add(`id:${uid}`);
      if (uno) unitHasReceipts.add(`no:${uno}`);
    });
    return unitOptions.filter((unit) => {
      const status = String(unit?.bookingStatus || '').toLowerCase();
      if (status === 'booked' || status === 'sold') return false;
      const uid = String(unit?.id || '').trim();
      const uno = String(unit?.unitNumber || '').trim();
      if (uid && unitHasReceipts.has(`id:${uid}`)) return true;
      if (uno && unitHasReceipts.has(`no:${uno}`)) return true;
      return false;
    });
  }, [txns, unitOptions]);

  const refundReceiptOptions = useMemo<RefundReceiptOption[]>(() => {
    if (!isCustomerRefundMode) return [];
    const selectedUnitId = String(unitId || '').trim();
    const selectedUnitNumber = String(unitNumber || '').trim();
    if (!selectedUnitId && !selectedUnitNumber) return [];
    return txns
      .filter((txn) => String(txn?.txnType || '').toLowerCase() === 'receipt')
      .filter((txn) => {
        const kind = String(txn?.receiptCategory || '').toLowerCase();
        return kind === 'customer' || kind === 'gst';
      })
      .filter((txn) => {
        const txnUnitId = String(txn?.unitId || '').trim();
        const txnUnitNumber = String(txn?.unitNumber || '').trim();
        if (selectedUnitId && txnUnitId) return selectedUnitId === txnUnitId;
        return selectedUnitNumber && txnUnitNumber && selectedUnitNumber === txnUnitNumber;
      })
      .map((txn) => {
        const id = String(txn.id || '').trim();
        const amountValue = Number(txn.amount || 0);
        const refunded = Number(refundAllocatedByReceiptId.get(id) || 0);
        const dateValue =
          tsToDate(txn.txnDate) ||
          parseLooseDate(String((txn as any)?.legacyChqDate || (txn as any)?.legacyTransactionDate || '').trim()) ||
          null;
        return {
          id,
          unitId: String(txn.unitId || '').trim(),
          unitNumber: String(txn.unitNumber || '').trim(),
          bookingId: String(txn.bookingId || '').trim(),
          txnDate: dateValue,
          dateLabel: dateValue ? formatDDMMYYYY(dateValue) : '—',
          amount: amountValue > 0 ? amountValue : 0,
          alreadyRefunded: refunded > 0 ? refunded : 0,
          refundableBalance: Math.max(0, (amountValue > 0 ? amountValue : 0) - (refunded > 0 ? refunded : 0)),
          receiptCategory: String(txn.receiptCategory || '').toLowerCase() === 'gst' ? 'gst' : 'customer',
          partyName: String(txn.partyName || '').trim(),
          referenceNo: String(txn.referenceNo || '').trim(),
        } as RefundReceiptOption;
      })
      .filter((row) => row.refundableBalance > 0)
      .sort((left, right) => {
        const leftTs = left.txnDate ? left.txnDate.getTime() : 0;
        const rightTs = right.txnDate ? right.txnDate.getTime() : 0;
        if (leftTs !== rightTs) return leftTs - rightTs;
        return left.id.localeCompare(right.id);
      });
  }, [isCustomerRefundMode, refundAllocatedByReceiptId, txns, unitId, unitNumber]);

  const refundUnitCustomerName = useMemo(() => {
    if (!isCustomerRefundMode) return '';
    const names = Array.from(
      new Set(
        refundReceiptOptions
          .map((row) => row.partyName)
          .filter((value) => !!String(value || '').trim())
      )
    );
    return names.join(', ');
  }, [isCustomerRefundMode, refundReceiptOptions]);

  const refundSelectedReceiptCapacity = useMemo(() => {
    if (!isCustomerRefundMode) return 0;
    const selected = new Set(refundAgainstReceiptIds.map((id) => String(id || '').trim()).filter(Boolean));
    return refundReceiptOptions.reduce((sum, row) => {
      if (!selected.has(row.id)) return sum;
      return sum + Number(row.refundableBalance || 0);
    }, 0);
  }, [isCustomerRefundMode, refundAgainstReceiptIds, refundReceiptOptions]);

  const refundAllocations = useMemo(() => {
    const rawAmount = Number(String(amount || '').replace(/,/g, ''));
    if (!isCustomerRefundMode || !(rawAmount > 0)) return [] as Array<{ txnId: string; amount: number; receiptCategory: 'customer' | 'gst' }>;
    const selectedIds = refundAgainstReceiptIds.map((id) => String(id || '').trim()).filter(Boolean);
    if (!selectedIds.length) return [] as Array<{ txnId: string; amount: number; receiptCategory: 'customer' | 'gst' }>;
    const byId = new Map(refundReceiptOptions.map((row) => [row.id, row]));
    let left = rawAmount;
    const rows: Array<{ txnId: string; amount: number; receiptCategory: 'customer' | 'gst' }> = [];
    selectedIds.forEach((id) => {
      if (!(left > 0)) return;
      const option = byId.get(id);
      if (!option) return;
      const maxRefund = Math.max(0, Number(option.refundableBalance || 0));
      if (!(maxRefund > 0)) return;
      const assigned = Math.min(maxRefund, left);
      rows.push({ txnId: id, amount: assigned, receiptCategory: option.receiptCategory });
      left -= assigned;
    });
    return rows;
  }, [amount, isCustomerRefundMode, refundAgainstReceiptIds, refundReceiptOptions]);

  const normalizedSearch = useMemo(
    () => normalizeSearchValue(search),
    [search]
  );
  const normalizedSearchCompact = useMemo(
    () => normalizeSearchCompact(search),
    [search]
  );

  const filtered = useMemo(() => {
    let rows = txns.slice();
    if (filterType !== 'all') rows = rows.filter((t) => (t.txnType || 'receipt') === filterType);
    if (reconcileFilter === 'reconciled') rows = rows.filter((t) => !txnIsUnreconciled(t));
    if (reconcileFilter === 'unreconciled') rows = rows.filter((t) => txnIsUnreconciled(t));
    if (filterBankId) rows = rows.filter((t) => String(t.bankAccountId || '') === filterBankId);
    if (!normalizedSearch) return rows;
    return rows.filter((t) => {
      const bank = t.bankAccountId ? bankById.get(String(t.bankAccountId)) : null;
      const amountNumber = Number(t.amount || 0) || 0;
      const amountFormatted = amountNumber.toLocaleString('en-IN');
      const amountWithDecimals = formatAmount2(amountNumber);
      const values = [
        t.amount,
        amountNumber,
        amountFormatted,
        amountWithDecimals,
        String(amountNumber),
        t.unitNumber,
        t.unitId,
        t.partyName,
        (t as any).payerBankName,
        t.referenceNo,
        t.mode,
        t.notes,
        bank?.name,
        bank?.bankName,
        bank?.holderName,
        bank?.accountLast4,
      ]
        .filter((value) => value != null && String(value).trim() !== '');
      const hay = values.map(normalizeSearchValue).join(' ');
      const compactHay = values.map(normalizeSearchCompact).join(' ');
      const words = normalizedSearch.split(' ').filter(Boolean);
      const phraseMatch = hay.includes(normalizedSearch) || (!!normalizedSearchCompact && compactHay.includes(normalizedSearchCompact));
      const wordMatch = words.length > 1 && words.every((word) => hay.includes(word) || compactHay.includes(normalizeSearchCompact(word)));
      return phraseMatch || wordMatch;
    });
  }, [bankById, filterBankId, filterType, normalizedSearch, normalizedSearchCompact, reconcileFilter, txns]);

  const sortedFiltered = useMemo(() => {
    const rows = filtered.slice();
    const multiplier = sortDir === 'asc' ? 1 : -1;
    const valueFor = (row: BankTransaction) => {
      if (sortBy === 'amount') return Number(row.amount || 0);
      if (sortBy === 'chqDate') {
        const parsed = bankReceiptDisplayDate(row);
        return parsed ? parsed.getTime() : 0;
      }
      const direct = tsToDate(row.txnDate);
      if (direct) return direct.getTime();
      const ymd = parseLooseDate(String((row as any)?.txnDateYMD || '').trim());
      if (ymd) return ymd.getTime();
      const legacy = parseLooseDate(String((row as any)?.legacyTransactionDate || '').trim());
      return legacy ? legacy.getTime() : 0;
    };
    rows.sort((left, right) => {
      const l = valueFor(left);
      const r = valueFor(right);
      if (l === r) return 0;
      return l > r ? multiplier : -multiplier;
    });
    return rows;
  }, [filtered, sortBy, sortDir]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedFiltered.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedFiltered]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedFiltered.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [page, rowsPerPage, sortedFiltered.length]);

  useEffect(() => {
    setPage(0);
  }, [filterType, reconcileFilter, filterBankId, normalizedSearch, sortBy, sortDir]);

  const onSortHeader = (key: 'transactionDate' | 'chqDate' | 'amount') => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDir('desc');
  };

  const eligibleBankAccounts = useMemo(() => {
    return bankAccounts.filter((b) => {
      const roleRaw = String(b.accountRole || '').toLowerCase();
      const allowCustomer =
        typeof b.allowCustomerReceipts === 'boolean' ? !!b.allowCustomerReceipts : roleRaw === 'collection';
      const allowGst =
        typeof b.allowGstReceipts === 'boolean'
          ? !!b.allowGstReceipts
          : roleRaw !== 'rera70' && roleRaw !== 'collection' && roleRaw !== 'dummy_advocate';
      const allowPay =
        typeof b.allowPayments === 'boolean'
          ? !!b.allowPayments
          : roleRaw === 'operating30' || roleRaw === 'other' || !roleRaw;

      if (txnType === 'payment') return allowPay;
      if (receiptCategory === 'customer') return allowCustomer;
      if (receiptCategory === 'gst') return allowGst;
      if (receiptCategory === 'advocate') return roleRaw === 'dummy_advocate';
      return allowGst;
    });
  }, [bankAccounts, receiptCategory, txnType]);

  const unreconciledCount = useMemo(
    () => txns.filter((t) => txnIsUnreconciled(t)).length,
    [txns]
  );
  const transferUsedReceiptIds = useMemo(() => {
    const used = new Set<string>();
    transferRequests.forEach((request) => {
      const status = String(request?.status || '').toLowerCase();
      if (status === 'draft') return;
      (request?.selectedReceiptIds || []).forEach((id) => {
        const value = String(id || '').trim();
        if (value) used.add(value);
      });
    });
    return used;
  }, [transferRequests]);
  const activeBookingByUnitId = useMemo(() => {
    const map = new Map<string, string>();
    unitOptions.forEach((row: any) => {
      const unitId = String(row?.id || '').trim();
      const activeBookingId = String(row?.activeBookingId || '').trim();
      if (unitId && activeBookingId) map.set(unitId, activeBookingId);
    });
    return map;
  }, [unitOptions]);
  const transferEligibleCustomerReceipts = useMemo(() => {
    return txns
      .filter((row) => String(row.txnType || '').toLowerCase() === 'receipt')
      .filter((row) => String(row.receiptCategory || '').toLowerCase() === 'customer')
      .filter((row) => {
        const unitId = String(row?.unitId || '').trim();
        if (!unitId) return false;
        const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
        if (!activeBookingId) return false;
        const txnBookingId = String((row as any)?.bookingId || '').trim();
        if (txnBookingId && txnBookingId !== activeBookingId) return false;
        return true;
      })
      .filter((row) => row?.fundTransferProcessed !== true)
      .filter((row) => String((row as any)?.fundTransferRequestId || '').trim().length === 0)
      .filter((row) => !transferUsedReceiptIds.has(String(row.id || '').trim()))
      .sort((left, right) => {
        const ld = bankReceiptDisplayDate(left) || new Date(0);
        const rd = bankReceiptDisplayDate(right) || new Date(0);
        return rd.getTime() - ld.getTime();
      });
  }, [activeBookingByUnitId, transferUsedReceiptIds, txns]);
  const transferEligibleReceiptsById = useMemo(() => {
    const map = new Map<string, BankTransaction>();
    transferEligibleCustomerReceipts.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [transferEligibleCustomerReceipts]);
  const transferSelectedReceiptRows = useMemo(
    () => transferSelectedReceiptIds.map((id) => transferEligibleReceiptsById.get(String(id))).filter(Boolean) as BankTransaction[],
    [transferEligibleReceiptsById, transferSelectedReceiptIds]
  );
  const transferSelectedReceiptTotal = useMemo(
    () => transferSelectedReceiptRows.reduce((sum, row) => sum + (Number(row.amount || 0) || 0), 0),
    [transferSelectedReceiptRows]
  );
  const transferProcessedCumulativeBase = useMemo(() => {
    return txns
      .filter((row: any) => row?.isActive !== false)
      .filter((row) => String(row.txnType || '').toLowerCase() === 'receipt')
      .filter((row) => String(row.receiptCategory || '').toLowerCase() === 'customer')
      .filter((row) => String(row.unitId || '').trim().length > 0)
      .filter((row) => row?.fundTransferProcessed === true)
      .filter((row) => {
        const unitId = String(row?.unitId || '').trim();
        const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
        if (!activeBookingId) return false;
        const txnBookingId = String((row as any)?.bookingId || '').trim();
        if (txnBookingId && txnBookingId !== activeBookingId) return false;
        return true;
      })
      .reduce((sum, row) => {
        const amount = Number(row.amount || 0);
        if (!Number.isFinite(amount) || amount <= 0) return sum;
        return sum + amount;
      }, 0);
  }, [activeBookingByUnitId, txns]);
  const transferRefundTotal = useMemo(() => {
    return txns
      .filter((row: any) => row?.isActive !== false)
      .filter((row) => String(row.txnType || '').toLowerCase() === 'payment')
      .filter((row) => String((row as any).paymentCategory || '').toLowerCase() === 'customer_refund')
      .filter((row) => String(row.unitId || '').trim().length > 0)
      .filter((row) => {
        const unitId = String(row?.unitId || '').trim();
        const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
        if (!activeBookingId) return false;
        const txnBookingId = String((row as any)?.bookingId || '').trim();
        if (txnBookingId && txnBookingId !== activeBookingId) return false;
        return true;
      })
      .reduce((sum, row) => {
        const amount = Number(row.amount || 0);
        if (!Number.isFinite(amount) || amount <= 0) return sum;
        return sum + amount;
      }, 0);
  }, [activeBookingByUnitId, txns]);
  const transferCumulativeTillDate = useMemo(
    () => Math.max(0, transferProcessedCumulativeBase - transferRefundTotal + transferSelectedReceiptTotal),
    [transferProcessedCumulativeBase, transferRefundTotal, transferSelectedReceiptTotal]
  );

  useEffect(() => {
    const suggested = transferSelectedReceiptTotal * 0.7;
    setTransferAmount(suggested > 0 ? formatAmount2(suggested) : '');
  }, [transferSelectedReceiptTotal]);
  const selectedBankRoleRaw = useMemo(() => {
    const selected = bankAccounts.find((b) => String(b.id || '') === String(bankAccountId || ''));
    return String(selected?.accountRole || '').toLowerCase();
  }, [bankAccountId, bankAccounts]);
  const isDummyAdvocateBankSelected = selectedBankRoleRaw === 'dummy_advocate';
  useEffect(() => {
    if (!isDummyAdvocateBankSelected && legalChargeType) {
      setLegalChargeType('');
    }
  }, [isDummyAdvocateBankSelected, legalChargeType]);
  const selectedUnitOption = useMemo(
    () =>
      (unitId ? unitOptions.find((u) => String(u.id) === String(unitId)) : null) ||
      (unitNumber ? unitOptions.find((u) => String(u.unitNumber) === String(unitNumber)) : null) ||
      null,
    [unitId, unitNumber, unitOptions]
  );
  useEffect(() => {
    if (!isCustomerRefundMode) {
      if (refundAgainstReceiptIds.length) setRefundAgainstReceiptIds([]);
      return;
    }
    const validIds = new Set(refundReceiptOptions.map((row) => row.id));
    const next = refundAgainstReceiptIds.filter((id) => validIds.has(String(id || '').trim()));
    if (next.length !== refundAgainstReceiptIds.length) {
      setRefundAgainstReceiptIds(next);
    }
  }, [isCustomerRefundMode, refundAgainstReceiptIds, refundReceiptOptions]);
  useEffect(() => {
    if (!isCustomerRefundMode) return;
    if (refundUnitCustomerName && !String(partyName || '').trim()) {
      setPartyName(refundUnitCustomerName);
    }
  }, [isCustomerRefundMode, partyName, refundUnitCustomerName]);
  const expectedAdvocateAmountForSelection = useMemo(() => {
    const unitAmount = Number(selectedUnitOption?.unitAmount || 0) || 0;
    const hasMaleCustomer = selectedUnitCustomers.some(
      (customer) => String(customer?.gender || '').trim().toLowerCase() === 'male'
    );
    if (legalChargeType === 'stamp_duty') {
      const fromUnit = Number(selectedUnitOption?.stampDuty || 0) || 0;
      return fromUnit > 0 ? fromUnit : unitAmount > 0 ? ceilHundred(ceilHundred(unitAmount) * 0.049) : 0;
    }
    if (legalChargeType === 'registration_charges') {
      const fromUnit = Number(selectedUnitOption?.registrationCharges || 0) || 0;
      return fromUnit > 0
        ? fromUnit
        : unitAmount <= 0
          ? 0
          : selectedUnitCustomers.length === 0 || hasMaleCustomer
            ? ceilHundred(ceilHundred(unitAmount) * 0.01) + 1200
            : 1200;
    }
    if (legalChargeType === 'agreement_fee') return Number(projectAdvocateAgreementFee || 0) || 0;
    if (legalChargeType === 'deed_fee') return Number(projectAdvocateDeedFee || 0) || 0;
    return 0;
  }, [
    legalChargeType,
    selectedUnitOption?.unitAmount,
    selectedUnitOption?.stampDuty,
    selectedUnitOption?.registrationCharges,
    selectedUnitCustomers,
    projectAdvocateAgreementFee,
    projectAdvocateDeedFee,
  ]);
  const paidAdvocateAmountForSelection = useMemo(() => {
    if (!(unitId || unitNumber) || !legalChargeType) return 0;
    return txns.reduce((sum, row) => {
      if (editingTxn?.id && String(row.id) === String(editingTxn.id)) return sum;
      if (String(row.txnType || '') !== 'receipt') return sum;
      if (String(row.receiptCategory || '') !== 'advocate') return sum;
      if (String(row.legalChargeType || '') !== String(legalChargeType)) return sum;
      const sameUnit = unitId
        ? String(row.unitId || '') === String(unitId)
        : String(row.unitNumber || '') === String(unitNumber || '');
      if (!sameUnit) return sum;
      return sum + (Number(row.amount || 0) || 0);
    }, 0);
  }, [editingTxn?.id, legalChargeType, txns, unitId, unitNumber]);
  const advocatePendingAmountForSelection = Math.max(
    0,
    Number(expectedAdvocateAmountForSelection || 0) - Number(paidAdvocateAmountForSelection || 0)
  );

  const prevReceiptCategoryRef = useRef(receiptCategory);
  useEffect(() => {
    if (lockedView) return;
    if (txnType !== 'receipt') return;
    const prev = prevReceiptCategoryRef.current;
    prevReceiptCategoryRef.current = receiptCategory;

    if (receiptCategory === 'customer') {
      const pick = eligibleBankAccounts.length === 1 ? eligibleBankAccounts[0] : null;
      if (pick) {
        const id = String(pick.id || '');
        if (id && bankAccountId !== id) setBankAccountId(id);
      }
      return;
    }

    if (prev === 'customer' && bankAccountId) setBankAccountId('');
  }, [bankAccountId, eligibleBankAccounts, lockedView, receiptCategory, txnType]);

  useEffect(() => {
    const shouldLinkUnit =
      txnType === 'receipt' &&
      (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate');
    if (lockedView) return;
    if (!shouldLinkUnit) return;
    if (!currentProjectId || !unitId) {
      if (partyName) setPartyName('');
      setSelectedUnitCustomers([]);
      return;
    }
    const unitMeta = unitOptions.find((u) => u.id === unitId);
    const bookingId = unitMeta?.activeBookingId ? String(unitMeta.activeBookingId) : '';
    if (!bookingId) return;
    (async () => {
      try {
        const customersCol = collection(
          db,
          'projects',
          currentProjectId,
          'units',
          unitId,
          'bookings',
          bookingId,
          'customers'
        );
        const snap = await getDocs(customersCol);
        if (snap.empty) {
          setSelectedUnitCustomers([]);
          return;
        }
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setSelectedUnitCustomers(rows);
        const primary = rows.find((c: any) => String(c.role || '').toLowerCase() === 'primary') || rows[0];
        const name = String(primary?.displayName || '').trim() ||
          [primary?.firstName, primary?.middleName, primary?.lastName].filter(Boolean).join(' ').trim();
        if (name) setPartyName(name);
      } catch (err) {
        console.error('Load unit customers failed', err);
        setSelectedUnitCustomers([]);
      }
    })();
  }, [currentProjectId, db, lockedView, partyName, receiptCategory, txnType, unitId, unitOptions]);

  useEffect(() => {
    if (lockedView) return;
    if (receiptCategory !== 'customer' && receiptCategory !== 'gst' && receiptCategory !== 'advocate') {
      if (partyName) setPartyName('');
      if (payerBankName) setPayerBankName('');
    }
  }, [lockedView, partyName, payerBankName, receiptCategory]);

  useEffect(() => {
    if (lockedView) return;
    if (txnType !== 'receipt') return;
    if (!(receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')) return;
    if (editingTxn) return;
    if (payerBankAutofillSuppressed) return;
    if (payerBankName.trim()) return;
    const match = txns.find((t) => {
      if (String(t.txnType || '') !== 'receipt') return false;
      if (String(t.receiptCategory || '') !== receiptCategory) return false;
      const bank = String((t as any).payerBankName || '').trim();
      if (!bank) return false;
      if (unitId) return String(t.unitId || '') === unitId;
      if (unitNumber) return String(t.unitNumber || '') === unitNumber;
      if (partyName.trim()) return String(t.partyName || '').trim().toLowerCase() === partyName.trim().toLowerCase();
      return true;
    });
    const nextBank = String((match as any)?.payerBankName || '').trim();
    if (nextBank) setPayerBankName(nextBank);
  }, [editingTxn, lockedView, partyName, payerBankAutofillSuppressed, payerBankName, receiptCategory, txnType, txns, unitId, unitNumber]);

  useEffect(() => {
    if (editingTxn) return;
    setPayerBankAutofillSuppressed(false);
  }, [editingTxn, receiptCategory, unitId, unitNumber]);

  const waSelectedTemplate = useMemo(
    () => waTemplates.find((t: any) => String(t?.id || '') === String(waTemplateId || '')) || null,
    [waTemplateId, waTemplates]
  );

  useEffect(() => {
    if (!waTemplates.length) {
      setWaTemplateId('');
      return;
    }
    const paymentTemplate =
      waTemplates.find((t: any) => /payment/i.test(String(t?.label || ''))) ||
      waTemplates.find((t: any) => /payment/i.test(String(t?.name || ''))) ||
      waTemplates[0];
    const currentExists = waTemplates.some((t: any) => String(t?.id || '') === String(waTemplateId || ''));
    if (!waTemplateId || !currentExists) setWaTemplateId(String(paymentTemplate?.id || ''));
  }, [waTemplateId, waTemplates]);

  useEffect(() => {
    if (!waSenders.length) {
      setWaSenderId('');
      return;
    }
    const preferred = waSenders.find((s: any) => s?.active && s?.isDefault) || waSenders.find((s: any) => s?.active) || waSenders[0];
    if (!waSenderId || !waSenders.some((s: any) => String(s?.id || '') === String(waSenderId))) {
      setWaSenderId(String(preferred?.id || ''));
    }
  }, [waSenderId, waSenders]);

  useEffect(() => {
    if (!waDialogOpen || !selectedTxn) return;
    if (waTo.trim()) return;
    setWaTo('');
  }, [selectedTxn, waDialogOpen, waTo]);

  const openWhatsAppReceiptDialog = (txn: BankTransaction) => {
    setSelectedTxn(txn);
    setWaTo('');
    setWaDialogOpen(true);
    setWaSendError(null);
    setWaSendSuccess(null);
  };

  const buildWaTemplateValues = (template: any, txn: BankTransaction): Record<string, string> => {
    const values: Record<string, string> = {};
    const vars = Array.isArray(template?.variables) ? template.variables : [];
    const preferredDate =
      parseLooseDate(String((txn as any)?.legacyChqDate || '').trim()) || tsToDate(txn?.txnDate);
    const formattedDate = preferredDate ? formatDDMMYYYY(preferredDate) : '';
    const amount = Number(txn?.amount || 0) || 0;
    const hasUnitNumber = !!String(txn?.unitNumber || '').trim();
    const paymentTypeLabel =
      String(txn?.receiptCategory || '').toLowerCase() === 'gst'
        ? 'GST Payment'
        : hasUnitNumber
          ? 'Property Payment'
          : String(txn?.mode || txn?.receiptCategory || txn?.txnType || '').trim();
    vars.forEach((entry: any) => {
      const key = String(entry?.key || '').trim();
      if (!key) return;
      const label = String(entry?.label || key).toLowerCase();
      const probe = `${key} ${label}`;
      if (/customer|party|name/.test(probe)) values[key] = String(txn?.partyName || '').trim();
      else if (/amount|amt|rupee|rs/.test(probe)) values[key] = amount ? amount.toLocaleString('en-IN') : '';
      else if (/date|txn/.test(probe)) values[key] = formattedDate;
      else if (/ref|cheq|chq|utr|no/.test(probe)) values[key] = String(txn?.referenceNo || '').trim();
      else if (/payment type|mode|type/.test(probe)) values[key] = paymentTypeLabel;
      else if (/project/.test(probe)) values[key] = String(projectName || '').trim();
      else if (/unit|flat/.test(probe)) values[key] = String(txn?.unitNumber || '').trim();
      else values[key] = String(entry?.defaultValue || '').trim();
    });
    return values;
  };

  const sendPaymentReceiptWhatsApp = async () => {
    if (!selectedTxn) return;
    return sendPaymentReceiptWhatsAppForTxn(selectedTxn, waSelectedTemplate);
  };

  const sendPaymentReceiptWhatsAppForTxn = async (txn: BankTransaction, templateOverride?: any) => {
    const activeTemplate = templateOverride || waSelectedTemplate;
    const toValue = String(waTo || '').replace(/\D/g, '');
    if (!toValue) {
      setWaSendError('Enter recipient number.');
      return;
    }
    if (!activeTemplate) {
      setWaSendError('Select template.');
      return;
    }
    setWaSending(true);
    setWaSendError(null);
    setWaSendSuccess(null);
    try {
      const values = buildWaTemplateValues(activeTemplate, txn);
      const vars = Array.isArray(activeTemplate?.variables) ? activeTemplate.variables : [];
      const templateComponents: any[] = [];
      const headerFormat = String(activeTemplate?.headerFormat || '').toUpperCase();
      const headerImageUrlRaw = String(activeTemplate?.headerImageUrl || '').trim();
      const selectedVoucherUrl = String(txn?.voucherUrl || '').trim();
      const headerImageUrl =
        headerImageUrlRaw === WHATSAPP_HEADER_PROJECT_LOGO_TOKEN
          ? String(projectLogoUrl || '').trim()
          : headerImageUrlRaw === WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN
            ? (selectedVoucherUrl || String(projectLogoUrl || '').trim())
          : headerImageUrlRaw;
      if (headerFormat === 'IMAGE') {
        if (!headerImageUrl) {
          setWaSendError('Selected template requires Header Image URL (Voucher/Project Logo URL is empty).');
          setWaSending(false);
          return;
        }
        templateComponents.push({
          type: 'header',
          parameters: [{ type: 'image', image: { link: headerImageUrl } }],
        });
      }
      if (vars.length) {
        templateComponents.push({
          type: 'body',
          parameters: vars.map((entry: any) => ({
            type: 'text',
            text: String(values[String(entry?.key || '').trim()] || '').trim() || '-',
          })),
        });
      }
      const call = httpsCallable(getFunctions(), 'whatsappSendMessage');
      await call({
        senderId: waSenderId || undefined,
        to: toValue,
        templateName: String(activeTemplate?.name || '').trim(),
        templateLanguage: String(activeTemplate?.language || 'en').trim() || 'en',
        templateComponents,
        projectId: currentProjectId || '',
        metadata: {
          source: 'office_banks_txn_detail',
          transactionId: String(txn.id || ''),
        },
      });
      setWaSendSuccess('Payment receipt WhatsApp queued.');
    } catch (err: any) {
      console.error('Send payment receipt WhatsApp failed', err);
      setWaSendError(err?.message || 'Failed to send WhatsApp.');
    } finally {
      setWaSending(false);
    }
  };

  const openDrawerForNew = () => {
    setSaveError(null);
    setEditingTxn(null);
    setTxnDetailOpen(false);
    setSelectedTxn(null);
    setLockedView(false);
    setEditableFields({
      bank: false,
      amount: false,
      referenceNo: false,
      txnType: false,
      receiptCategory: false,
    });
    setTxnType('receipt');
    setReceiptCategory('customer');
    setIsLoanDisbursement(false);
    setPaymentCategory('');
    setLegalChargeType('');
    setBankAccountId('');
    setTxnDateYMD(ymdToday());
    setAmount('');
    setPartyName('');
    setPayerBankName('');
    setPayerBankAutofillSuppressed(false);
    setUnitId('');
    setUnitNumber('');
    setMode('');
    setReferenceNo('');
    setNotes('');
    setRefundAgainstReceiptIds([]);
    setVoucherFile(null);
    setTxnDrawerOpen(true);
  };

  const openDrawerForCustomerRefund = () => {
    setBankActionsAnchorEl(null);
    setSaveError(null);
    setEditingTxn(null);
    setTxnDetailOpen(false);
    setSelectedTxn(null);
    setLockedView(false);
    setEditableFields({
      bank: false,
      amount: false,
      referenceNo: false,
      txnType: false,
      receiptCategory: false,
    });
    setTxnType('payment');
    setReceiptCategory('customer');
    setPaymentCategory('customer_refund');
    setLegalChargeType('');
    setBankAccountId('');
    setTxnDateYMD(ymdToday());
    setAmount('');
    setPartyName('');
    setPayerBankName('');
    setPayerBankAutofillSuppressed(false);
    setUnitId('');
    setUnitNumber('');
    setMode('');
    setReferenceNo('');
    setNotes('');
    setRefundAgainstReceiptIds([]);
    setVoucherFile(null);
    setTxnDrawerOpen(true);
  };

  const prepareImportCsvFile = async (file: File | null) => {
    if (!file) return;
    if (!currentProjectId) {
      setLoadError('Project or user not available.');
      return;
    }
    setLoadError(null);
    setImportSummary(null);
    setImportingCsv(true);
    try {
      const text = await file.text();
      const rows = parseCsvRows(text);
      if (rows.length < 2) throw new Error('CSV has no data rows.');
      const header = rows[0].map((h) => String(h || '').trim());
      const headerIndex: Record<string, number> = {};
      header.forEach((h, idx) => {
        headerIndex[h] = idx;
      });
      const required = [
        'id',
        'Account',
        'Chq Date',
        'Transaction Date',
        'GST?',
        'Unit No',
        'Ref / Chq No',
        'Deposit',
        'Withdraw',
        'Bank',
        'Description',
      ];
      const missing = required.filter((k) => typeof headerIndex[k] !== 'number');
      if (missing.length) throw new Error(`Missing CSV columns: ${missing.join(', ')}`);
      const sourceAccounts = Array.from(
        new Set(
          rows
            .slice(1)
            .map((r) => String(r[headerIndex['Account']] || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      const defaults: Record<string, string> = {};
      sourceAccounts.forEach((acc) => {
        defaults[acc] = bankIdByAnyName.get(normalizeKey(acc)) || '';
      });
      setPendingImport({
        fileName: file.name,
        rows,
        headerIndex,
        sourceAccounts,
      });
      setPendingAccountMapping(defaults);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to import CSV.');
    } finally {
      setImportingCsv(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const commitPendingImport = async () => {
    if (!pendingImport) return;
    if (!currentProjectId || !currentUser?.email) {
      setLoadError('Project or user not available.');
      return;
    }
    const unresolved = pendingImport.sourceAccounts.filter((acc) => !pendingAccountMapping[acc]);
    if (unresolved.length > 0) {
      setLoadError(`Map all Account values before import: ${unresolved.join(', ')}`);
      return;
    }
    setLoadError(null);
    setImportSummary(null);
    setImportingCsv(true);
    try {
      const unitCustomerNameCache = new Map<string, string>();
      const resolveUnitCustomerName = async (unitMeta?: { id: string; activeBookingId?: string | null }) => {
        const uid = String(unitMeta?.id || '').trim();
        if (!uid) return '';
        if (unitCustomerNameCache.has(uid)) return String(unitCustomerNameCache.get(uid) || '');
        const bookingId = String(unitMeta?.activeBookingId || '').trim();
        if (!bookingId) {
          unitCustomerNameCache.set(uid, '');
          return '';
        }
        try {
          const snap = await getDocs(
            collection(
              db,
              'projects',
              currentProjectId,
              'units',
              uid,
              'bookings',
              bookingId,
              'customers'
            )
          );
          const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          const primary =
            rows.find((c: any) => String(c.role || '').toLowerCase() === 'primary') || rows[0];
          const resolved =
            String(primary?.displayName || '').trim() ||
            [primary?.firstName, primary?.middleName, primary?.lastName]
              .filter(Boolean)
              .join(' ')
              .trim();
          unitCustomerNameCache.set(uid, resolved || '');
          return resolved || '';
        } catch {
          unitCustomerNameCache.set(uid, '');
          return '';
        }
      };

      let imported = 0;
      let skipped = 0;
      let failed = 0;
      let batch = writeBatch(db);
      let batchCount = 0;
      for (let r = 1; r < pendingImport.rows.length; r += 1) {
        const cells = pendingImport.rows[r];
        const get = (k: string) =>
          String(cells[pendingImport.headerIndex[k] ?? 0] || '').trim();
        const legacyId = get('id');
        const accountRaw = get('Account');
        const bankId = pendingAccountMapping[accountRaw] || '';
        if (!bankId) {
          skipped += 1;
          continue;
        }
        const deposit = parseLooseNumber(get('Deposit'));
        const withdraw = parseLooseNumber(get('Withdraw'));
        if (deposit > 0 && withdraw > 0) {
          failed += 1;
          continue;
        }
        const txnType: TxnType = withdraw > 0 ? 'payment' : 'receipt';
        const txnAmount = withdraw > 0 ? withdraw : deposit;
        if (!(txnAmount > 0)) {
          skipped += 1;
          continue;
        }
        const parsedTransactionDate = parseLooseDate(get('Transaction Date'));
        const unitNo = get('Unit No');
        const matchedUnit = unitByNumber.get(normalizeKey(unitNo));
        const customerName = unitNo ? await resolveUnitCustomerName(matchedUnit) : '';
        const descriptionName = get('Description');
        const mappedPartyName = customerName || descriptionName || null;
        const isGstRaw = normalizeKey(get('GST?'));
        const mappedReceiptCategory =
          txnType === 'receipt'
            ? isGstRaw === 'yes' || isGstRaw === 'true' || isGstRaw === '1'
              ? 'gst'
              : 'customer'
            : null;

        const docId = legacyId
          ? `legacy_${String(legacyId).replace(/[^a-zA-Z0-9_-]+/g, '_')}`
          : doc(collection(db, 'projects', currentProjectId, 'bankTransactions')).id;
        const hasTransactionDate = !!parsedTransactionDate;
        const refDoc = doc(db, 'projects', currentProjectId, 'bankTransactions', docId);
        batch.set(
          refDoc,
          {
            bankAccountId: bankId,
            txnType,
            receiptCategory: mappedReceiptCategory,
            unitId: matchedUnit?.id || null,
            unitNumber: matchedUnit?.unitNumber || (unitNo || null),
            amount: txnAmount,
            txnDate: parsedTransactionDate ? Timestamp.fromDate(parsedTransactionDate) : null,
            referenceNo: get('Ref / Chq No') || null,
            partyName: mappedPartyName,
            payerBankName: get('Bank') || null,
            notes: descriptionName || null,
            legacyChqDate: get('Chq Date') || null,
            legacyTransactionDate: get('Transaction Date') || null,
            isReconciled: hasTransactionDate,
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser.email,
            importSource: 'legacy_csv',
            legacyId: legacyId || null,
          },
          { merge: true }
        );
        batchCount += 1;
        imported += 1;
        if (batchCount >= 300) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      if (batchCount > 0) await batch.commit();
      setImportSummary(`Import completed. Imported: ${imported}, Skipped: ${skipped}, Failed: ${failed}.`);
      setPendingImport(null);
      setPendingAccountMapping({});
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to import CSV.');
    } finally {
      setImportingCsv(false);
    }
  };

  const openDrawerForEdit = (t: BankTransaction) => {
    setSaveError(null);
    setEditingTxn(t);
    setTxnDetailOpen(false);
    setLockedView(true);
    setEditableFields({
      bank: false,
      amount: false,
      referenceNo: false,
      txnType: false,
      receiptCategory: false,
    });
    setTxnType((t.txnType || 'receipt') as TxnType);
    setReceiptCategory((t.receiptCategory || 'customer') as any);
    setIsLoanDisbursement(t.isLoanDisbursement === true);
    setPaymentCategory(String((t as any)?.paymentCategory || '').trim());
    setLegalChargeType(
      (String(t.legalChargeType || '') as
        | ''
        | 'stamp_duty'
        | 'registration_charges'
        | 'agreement_fee'
        | 'deed_fee')
    );
    setBankAccountId(String(t.bankAccountId || ''));
    const d = parseLooseDate(String(t.legacyChqDate || '').trim()) || tsToDate(t.txnDate);
    setTxnDateYMD(d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : ymdToday());
    setAmount(typeof t.amount === 'number' ? String(t.amount) : '');
    setPartyName(String(t.partyName || ''));
    setPayerBankName(String((t as any).payerBankName || ''));
    setPayerBankAutofillSuppressed(true);
    setUnitId(String(t.unitId || ''));
    setUnitNumber(String(t.unitNumber || ''));
    setMode(String(t.mode || ''));
    setReferenceNo(String(t.referenceNo || ''));
    setNotes(String(t.notes || ''));
    const existingAllocations = Array.isArray((t as any)?.refundAllocations)
      ? (t as any).refundAllocations
      : [];
    const allocationIds = existingAllocations
      .map((row: any) => String(row?.txnId || '').trim())
      .filter(Boolean);
    const fallbackRefundIds = [
      String((t as any)?.refundAgainstTxnId || '').trim(),
      ...(Array.isArray((t as any)?.refundAgainstTxnIds)
        ? (t as any).refundAgainstTxnIds.map((id: any) => String(id || '').trim())
        : []),
    ].filter(Boolean);
    setRefundAgainstReceiptIds(
      Array.from(new Set(allocationIds.length ? allocationIds : fallbackRefundIds))
    );
    setVoucherFile(null);
    setTxnDrawerOpen(true);
  };

  const openDrawerForDetail = (t: BankTransaction) => {
    setSelectedTxn(t);
    setTxnDetailOpen(true);
  };

  const openTransferRequestDialog = () => {
    setTransferError(null);
    setTransferSuccess(null);
    setTransferGeneratedRequest(null);
    setTransferSelectedReceiptIds([]);
    setTransferAmount('');
    setTransferDialogOpen(true);
  };

  const openTransferHistoryDialog = () => {
    setBankActionsAnchorEl(null);
    setTransferHistoryDialogOpen(true);
  };

  const generateTransferRequestDocument = async () => {
    setTransferError(null);
    setTransferSuccess(null);
    if (!currentProjectId) return setTransferError('Select project first.');
    if (!currentUser?.email) return setTransferError('Not signed in.');
    if (!transferSelectedReceiptIds.length) return setTransferError('Select at least one customer receipt.');
    const requestAmount = parseLooseNumber(transferAmount);
    if (!Number.isFinite(requestAmount) || requestAmount <= 0) {
      return setTransferError('Enter valid transfer request amount.');
    }
    const cumulativeTillDate = transferCumulativeTillDate;
    if (!Number.isFinite(cumulativeTillDate) || cumulativeTillDate < 0) {
      return setTransferError('Enter valid total customer receipt till date.');
    }
    const selectedTemplate =
      projectDocumentTemplates.find(
        (row: any) => String(row?.templateId || '').trim() === FUND_TRANSFER_DOC_TEMPLATE_ID
      ) ||
      projectDocumentTemplates.find((row: any) => String(row?.id || '').trim() === FUND_TRANSFER_DOC_TEMPLATE_ID) ||
      null;
    if (!selectedTemplate) {
      return setTransferError(`Document template not found: ${FUND_TRANSFER_DOC_TEMPLATE_ID}`);
    }

    setTransferGenerating(true);
    try {
      const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
      const projectRow = projectSnap.exists() ? (projectSnap.data() as any) : {};
      const cpId = String(projectRow?.companyProfileId || '').trim();
      const companySnap = cpId ? await getDoc(doc(db, 'companyProfiles', cpId)) : null;
      const companyRow = companySnap?.exists() ? (companySnap.data() as any) : {};

      const issueDate = formatDDMMYYYY(new Date());
      const prefixRaw = String(selectedTemplate?.visualModel?.refPrefix || 'TRF');
      const refNo = await ensureTemplateSequenceRefNo({
        db,
        templateKey: `transfer_request_${String(selectedTemplate?.templateId || selectedTemplate?.id || 'v1')}`,
        refPrefix: normalizeOfficeRefPrefix(prefixRaw || 'TRF'),
      });
      const selectedTotal = transferSelectedReceiptTotal;

      const replacements: Record<string, string> = {
        '{{project_name}}': String(projectRow?.name || projectName || ''),
        '{{company_name}}': String(companyRow?.name || ''),
        '{{issue_date}}': issueDate,
        '{{ref_no}}': refNo,
        '{{transfer_request_ref_no}}': refNo,
        '{{transfer_request_amount}}': formatAmount2(requestAmount),
        '{{project_receipt_cumulative_till_date}}': formatAmount2(cumulativeTillDate),
        '{{selected_receipt_total}}': formatAmount2(selectedTotal),
        '{{company_logo_url}}': String(resolveCompanyLogoForTemplate(companyRow, selectedTemplate) || ''),
        '{{project_logo_url}}': resolveProjectLogoForTemplate(projectRow, selectedTemplate),
        '{{project_document_image_url}}': String(projectRow?.documentImageUrl || ''),
        '{{company_address}}': String(companyRow?.address || ''),
        '{{company_mobile}}': String(companyRow?.mobile || ''),
        '{{company_email}}': String(companyRow?.email || ''),
      };

      const templateHtml = String(
        selectedTemplate?.visualModel?.body ||
          selectedTemplate?.bodyHtml ||
          selectedTemplate?.renderHtml ||
          selectedTemplate?.visualModel?.renderHtml ||
          ''
      ).trim();
      if (!templateHtml) throw new Error('Template content is empty.');
      const rendered = applyTemplateTokens(templateHtml, replacements);
      const htmlToRender = /<html[\s>]/i.test(rendered)
        ? rendered
        : `<!doctype html><html><head><meta charset="utf-8" /><title>Transfer Request</title></head><body>${rendered}</body></html>`;

      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const vm = selectedTemplate?.visualModel || {};
      const runtimeVm = {
        ...vm,
        backgroundImageUrl: String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || projectBackgroundImageUrl || ''),
        backgroundOpacity:
          (vm as any)?.backgroundOpacity ??
          (String((vm as any)?.backgroundImageUrl || projectRow?.backgroundImageUrl || projectBackgroundImageUrl || '').trim() ? 0.21 : 0),
        issueDate,
        refNo,
      };
      const result = await renderTemplatePdf(
        buildPdfPayloadFromTemplate({
          html: htmlToRender,
          tokenValues: replacements,
          visualModel: runtimeVm,
          companyLogoUrl: String(resolveCompanyLogoForTemplate(companyRow, selectedTemplate) || ''),
          projectLogoUrl: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
          companyAddress: String(companyRow?.address || ''),
          companyMobile: String(companyRow?.mobile || ''),
          companyEmail: String(companyRow?.email || ''),
          companyFontFamily: String(companyRow?.fontFamily || ''),
          companyFontUrl: String(companyRow?.fontUrl || ''),
          companyFontFormat: String(companyRow?.fontFormat || ''),
          gujaratiFontFamily: String(companyRow?.gujaratiFontFamily || ''),
          gujaratiFontUrl: String(companyRow?.gujaratiFontUrl || ''),
          gujaratiFontFormat: String(companyRow?.gujaratiFontFormat || ''),
          paperSize: String(vm.paperSize || 'A4'),
          fileName: `transfer_request_${Date.now()}`,
        })
      );
      const data = (result?.data || {}) as {
        mimeType?: string;
        pdfBytes?: number[] | Record<string, number>;
        pdfBase64?: string | { type?: string; data?: number[] };
      };
      const rawBytes = Array.isArray(data.pdfBytes)
        ? data.pdfBytes
        : data.pdfBytes && typeof data.pdfBytes === 'object'
          ? Object.keys(data.pdfBytes)
              .sort((a, b) => Number(a) - Number(b))
              .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
          : [];
      let pdfBuffer = rawBytes.length ? Uint8Array.from(rawBytes) : new Uint8Array();
      if (!pdfBuffer.length) {
        const base64Raw =
          typeof data.pdfBase64 === 'string'
            ? data.pdfBase64
            : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
              ? (() => {
                  const source = data.pdfBase64?.data || [];
                  let chunk = '';
                  for (let i = 0; i < source.length; i += 0x8000) {
                    chunk += String.fromCharCode(...source.slice(i, i + 0x8000));
                  }
                  return btoa(chunk);
                })()
              : '';
        const normalizedBase64 = base64Raw.replace(/^data:application\/pdf;base64,/i, '').replace(/\s+/g, '');
        if (normalizedBase64) {
          const binary = atob(normalizedBase64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          pdfBuffer = bytes;
        }
      }
      if (!pdfBuffer.length) throw new Error('Invalid PDF payload from server.');

      const datePrefix = String(issueDate || '').replace(/\//g, '-');
      const projectNameForFile = String(projectRow?.name || projectName || 'Swarna Avsar').trim() || 'Swarna Avsar';
      const finalFileName = `${datePrefix}_Fund Transfer Request_${projectNameForFile}.pdf`;
      const requestRef = doc(collection(db, 'projects', currentProjectId, 'transferRequests'));
      const fileDisplayName = finalFileName;
      const safeName = fileDisplayName.replace(/[^\w.-]+/g, '_');
      const path = `transferRequests/${currentProjectId}/${requestRef.id}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pdfBuffer, {
        contentType: data.mimeType || 'application/pdf',
        cacheControl: 'no-store, max-age=0',
      });
      const baseUrl = await getDownloadURL(storageRef);
      const fileUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
      const selectedRows = transferSelectedReceiptRows.map((row) => ({
        id: row.id,
        unitNo: String(row.unitNumber || '').trim(),
        amount: Number(row.amount || 0) || 0,
        date:
          String((row as any)?.legacyChqDate || (row as any)?.txnDateYMD || (row as any)?.legacyTransactionDate || '').trim() ||
          (() => {
            const date = tsToDate(row.txnDate);
            return date ? formatDDMMYYYY(date) : '';
          })(),
        referenceNo: String(row.referenceNo || '').trim(),
        partyName: String(row.partyName || '').trim(),
      }));

      const payload: any = {
        status: 'generated',
        selectedReceiptIds: transferSelectedReceiptIds,
        selectedReceipts: selectedRows,
        selectedReceiptTotal: selectedTotal,
        transferRequestAmount: requestAmount,
        projectReceiptCumulativeTillDate: cumulativeTillDate,
        refNo,
        issueDate,
        templateId: String(selectedTemplate?.templateId || selectedTemplate?.id || ''),
        templateDocId: String(selectedTemplate?.id || ''),
        templateName: String(selectedTemplate?.name || selectedTemplate?.templateId || ''),
        tokenValues: {
          project_name: String(projectRow?.name || projectName || ''),
          company_name: String(companyRow?.name || ''),
          issue_date: issueDate,
          ref_no: refNo,
          transfer_request_ref_no: refNo,
          transfer_request_amount: formatAmount2(requestAmount),
          project_receipt_cumulative_till_date: formatAmount2(cumulativeTillDate),
          selected_receipt_total: formatAmount2(selectedTotal),
          company_logo_url: String(companyRow?.logoUrl || ''),
          project_logo_url: resolveProjectLogoForTemplate(projectRow, selectedTemplate),
          project_document_image_url: String(projectRow?.documentImageUrl || ''),
        },
        file: {
          name: fileDisplayName,
          originalName: fileDisplayName,
          url: fileUrl,
          path,
          uploadedAtMs: Date.now(),
          uploadedBy: currentUser.email,
        },
        generatedAt: serverTimestamp(),
        generatedBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      };
      await setDoc(requestRef, payload, { merge: true });

      if (transferSelectedReceiptIds.length) {
        const txBatch = writeBatch(db);
        transferSelectedReceiptIds.forEach((txnId) => {
          const cleanTxnId = String(txnId || '').trim();
          if (!cleanTxnId) return;
          const txnRef = doc(db, 'projects', currentProjectId, 'bankTransactions', cleanTxnId);
          txBatch.set(
            txnRef,
            {
              fundTransferProcessed: true,
              fundTransferProcessedAt: serverTimestamp(),
              fundTransferRequestId: requestRef.id,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser.email,
            },
            { merge: true }
          );
        });
        await txBatch.commit();
      }

      const created: TransferRequestDoc = {
        id: requestRef.id,
        ...(payload as any),
      };
      setTransferGeneratedRequest(created);
      setTransferSuccess('Transfer request document generated.');
    } catch (err: any) {
      console.error('Generate transfer request failed', err);
      setTransferError(err?.message || 'Failed to generate transfer request document.');
    } finally {
      setTransferGenerating(false);
    }
  };

  const sendTransferRequestEmail = async (opts?: { toOverride?: string[] }) => {
    setTransferError(null);
    setTransferSuccess(null);
    if (!currentProjectId) return setTransferError('Select project first.');
    if (!currentUser?.email) return setTransferError('Not signed in.');
    const transferRequestId = String(transferGeneratedRequest?.id || '').trim();
    if (!transferRequestId) return setTransferError('Generate transfer request document first.');
    const templateKey = FUND_TRANSFER_EMAIL_TEMPLATE_KEY;
    const templateExists = projectEmailTemplates.some(
      (row: any) => String(row?.templateKey || '').trim() === templateKey
    );
    if (!templateExists) return setTransferError(`Email template not found: ${templateKey}`);
    const requestedOverride = Array.isArray(opts?.toOverride) ? opts?.toOverride : undefined;
    const toOverride = (requestedOverride || [])
      .map((x) => String(x || '').trim())
      .filter(Boolean);

    setTransferSending(true);
    try {
      const callable = httpsCallable(getFunctions(), 'sendTransferRequestEmail');
      await callable({
        projectId: currentProjectId,
        transferRequestId,
        templateKey,
        ...(toOverride.length ? { toOverride } : {}),
      });
      await updateDoc(doc(db, 'projects', currentProjectId, 'transferRequests', transferRequestId), {
        status: 'emailed',
        emailedAt: serverTimestamp(),
        emailedBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setTransferSuccess('Transfer request email sent.');
      return true;
    } catch (err: any) {
      console.error('Send transfer request email failed', err);
      setTransferError(err?.message || 'Failed to send transfer request email.');
      return false;
    } finally {
      setTransferSending(false);
    }
  };

  const openTransferEmailPreview = async () => {
    setTransferError(null);
    setTransferSuccess(null);
    setTransferEmailPreviewError(null);
    setTransferEmailPreviewData(null);
    if (!currentProjectId) return setTransferError('Select project first.');
    if (!currentUser?.email) return setTransferError('Not signed in.');
    const transferRequestId = String(transferGeneratedRequest?.id || '').trim();
    if (!transferRequestId) return setTransferError('Generate transfer request document first.');
    const templateKey = FUND_TRANSFER_EMAIL_TEMPLATE_KEY;
    const templateExists = projectEmailTemplates.some(
      (row: any) => String(row?.templateKey || '').trim() === templateKey
    );
    if (!templateExists) return setTransferError(`Email template not found: ${templateKey}`);

    setTransferEmailPreviewOpen(true);
    setTransferEmailPreviewLoading(true);
    try {
      const callable = httpsCallable(getFunctions(), 'previewTransferRequestEmail');
      const result = await callable({
        projectId: currentProjectId,
        transferRequestId,
        templateKey,
      });
      const data = (result?.data || {}) as any;
      const preview: TransferEmailPreview = {
        templateName: String(data?.templateName || templateKey),
        to: Array.isArray(data?.to) ? data.to.map((v: any) => String(v || '').trim()).filter(Boolean) : [],
        subject: String(data?.subject || ''),
        bodyHtml: String(data?.bodyHtml || ''),
        attachments: Array.isArray(data?.attachments)
          ? data.attachments.map((row: any) => ({
            key: String(row?.key || ''),
            name: String(row?.name || ''),
            source: String(row?.source || ''),
          }))
          : [],
      };
      setTransferEmailPreviewData(preview);
      setTransferEmailToDraft((preview.to || []).join(', '));
    } catch (err: any) {
      console.error('Load transfer request email preview failed', err);
      setTransferEmailPreviewError(err?.message || 'Failed to load email preview.');
    } finally {
      setTransferEmailPreviewLoading(false);
    }
  };

  const confirmAndSendTransferEmail = async () => {
    setTransferEmailPreviewError(null);
    const toOverride = String(transferEmailToDraft || '')
      .split(',')
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    if (!toOverride.length) {
      setTransferEmailPreviewError('Enter recipient email.');
      return;
    }
    const ok = await sendTransferRequestEmail({ toOverride });
    if (!ok) return;
    setTransferEmailPreviewOpen(false);
  };

  const detectDuplicateMatchesForCurrentForm = useCallback((input: {
    amount: number;
    date: Date;
  }): DuplicateMatch[] => {
    const selectedTxnId = String(editingTxn?.id || '').trim();
    const normalizedRef = normalizeDupRef(referenceNo);
    const normalizedParty = normalizeDupText(partyName);
    const normalizedPayerBank = normalizeDupText(payerBankName);
    const normalizedMode = normalizeDupText(mode);
    const normalizedPaymentCategory = String(paymentCategory || '').trim().toLowerCase();
    const normalizedReceiptCategory = String(receiptCategory || '').trim().toLowerCase();
    const normalizedUnitId = String(unitId || '').trim();
    const normalizedBookingId = String((editingTxn as any)?.bookingId || '').trim() || String(
      (
        (normalizedUnitId
          ? unitOptions.find((u) => String(u.id || '').trim() === normalizedUnitId)
          : null) ||
        (unitNumber
          ? unitOptions.find((u) => String(u.unitNumber || '').trim() === String(unitNumber || '').trim())
          : null)
      )?.activeBookingId || ''
    ).trim();

    const candidates = txns
      .filter((row) => String(row.id || '').trim() && String(row.id || '').trim() !== selectedTxnId)
      .filter((row) => String(row.txnType || 'receipt').toLowerCase() === String(txnType || 'receipt').toLowerCase())
      .filter((row) => {
        if (String(txnType || '').toLowerCase() === 'receipt') {
          return String(row.receiptCategory || '').toLowerCase() === normalizedReceiptCategory;
        }
        return String((row as any).paymentCategory || '').toLowerCase() === normalizedPaymentCategory;
      })
      .filter((row) => String(row.bankAccountId || '').trim() === String(bankAccountId || '').trim())
      .filter((row) => Math.abs(Number(row.amount || 0) - input.amount) < 0.01)
      .filter((row) => {
        if (normalizedUnitId && String(row.unitId || '').trim() !== normalizedUnitId) return false;
        if (!normalizedUnitId && String(unitNumber || '').trim()) {
          return String(row.unitNumber || '').trim() === String(unitNumber || '').trim();
        }
        return true;
      })
      .filter((row) => {
        if (!normalizedBookingId) return true;
        const rowBookingId = String((row as any).bookingId || '').trim();
        return !rowBookingId || rowBookingId === normalizedBookingId;
      });

    const results: DuplicateMatch[] = [];
    candidates.forEach((row) => {
      const rowDate = coerceTxnDateForDuplicate(row);
      if (!rowDate) return;
      const diff = dayDiffAbs(input.date, rowDate);
      if (diff > 10) return;

      const rowRef = normalizeDupRef((row as any).referenceNo);
      const rowParty = normalizeDupText((row as any).partyName);
      const rowPayerBank = normalizeDupText((row as any).payerBankName);
      const rowMode = normalizeDupText((row as any).mode);
      const hasRefMatch = !!normalizedRef && !!rowRef && normalizedRef === rowRef;
      const hasPartyMatch = !!normalizedParty && !!rowParty && normalizedParty === rowParty;
      const hasPayerBankMatch = !!normalizedPayerBank && !!rowPayerBank && normalizedPayerBank === rowPayerBank;
      const hasModeMatch = !!normalizedMode && !!rowMode && normalizedMode === rowMode;
      if (!hasRefMatch && !hasPartyMatch && !hasPayerBankMatch && !hasModeMatch) return;

      const isExact =
        diff === 0 &&
        String(row.bankAccountId || '').trim() === String(bankAccountId || '').trim() &&
        (hasRefMatch || (hasPartyMatch && hasPayerBankMatch) || hasModeMatch);

      results.push({
        kind: isExact ? 'exact' : 'likely',
        row,
        dateText: formatDDMMYYYY(rowDate),
        dayDiff: diff,
      });
    });

    return results.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'exact' ? -1 : 1;
      return a.dayDiff - b.dayDiff;
    });
  }, [
    editingTxn,
    referenceNo,
    partyName,
    payerBankName,
    mode,
    paymentCategory,
    receiptCategory,
    unitId,
    unitOptions,
    unitNumber,
    txns,
    txnType,
    bankAccountId,
  ]);

  useEffect(() => {
    if (!txnDrawerOpen || duplicateDialogOpen) {
      setLiveDuplicateMatches([]);
      return;
    }
    const amt = Number(String(amount || '').replace(/,/g, ''));
    const d = dateFromYMD(txnDateYMD);
    const hasIdentityField =
      !!String(referenceNo || '').trim() ||
      !!String(partyName || '').trim() ||
      !!String(payerBankName || '').trim() ||
      !!String(mode || '').trim();
    if (!bankAccountId || !Number.isFinite(amt) || amt <= 0 || !d || !hasIdentityField) {
      setLiveDuplicateMatches([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setLiveDuplicateMatches(detectDuplicateMatchesForCurrentForm({ amount: amt, date: d }));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    txnDrawerOpen,
    duplicateDialogOpen,
    amount,
    txnDateYMD,
    bankAccountId,
    txnType,
    receiptCategory,
    paymentCategory,
    referenceNo,
    partyName,
    payerBankName,
    mode,
    unitId,
    unitNumber,
    editingTxn?.id,
    txns,
    unitOptions,
    detectDuplicateMatchesForCurrentForm,
  ]);

  const saveTxn = async (opts?: { sendAfterSave?: boolean; forceDuplicateOverride?: boolean }) => {
    setSaveError(null);
    if (!currentProjectId) return setSaveError('Select a project first.');
    if (!currentUser?.email) return setSaveError('Not signed in.');
    if (!bankAccountId) return setSaveError('Select a bank account.');
    const amt = Number(String(amount || '').replace(/,/g, ''));
    if (!Number.isFinite(amt) || amt <= 0) return setSaveError('Enter a valid amount.');
    const d = dateFromYMD(txnDateYMD);
    if (!d) return setSaveError('Select a valid date.');
    const chqDateText = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

    const selectedBank = bankAccountId ? bankAccounts.find((b) => b.id === bankAccountId) || null : null;
    const roleRaw = String(selectedBank?.accountRole || '').toLowerCase();
    const allowCustomer =
      typeof selectedBank?.allowCustomerReceipts === 'boolean'
        ? !!selectedBank.allowCustomerReceipts
        : roleRaw === 'collection';
    const allowGst =
      typeof selectedBank?.allowGstReceipts === 'boolean'
        ? !!selectedBank.allowGstReceipts
        : roleRaw !== 'rera70' && roleRaw !== 'collection' && roleRaw !== 'dummy_advocate';
    const allowPay =
      typeof selectedBank?.allowPayments === 'boolean'
        ? !!selectedBank.allowPayments
        : roleRaw === 'operating30' || roleRaw === 'other' || !roleRaw;

    if (txnType === 'payment' && !allowPay) return setSaveError('This bank account is not allowed for payments.');
    if (txnType === 'receipt' && receiptCategory === 'customer' && !allowCustomer) {
      return setSaveError('Customer receipts are allowed only in the Collection account.');
    }
    if (txnType === 'receipt' && receiptCategory === 'gst' && !allowGst) {
      return setSaveError('GST receipts are not allowed in this bank account.');
    }
    if (txnType === 'receipt' && receiptCategory === 'advocate' && roleRaw !== 'dummy_advocate') {
      return setSaveError('Advocate receipts are allowed only in Dummy Advocate account.');
    }
    if (
      txnType === 'receipt' &&
      (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate') &&
      !String(unitNumber || '').trim()
    ) {
      return setSaveError('Unit number is required for Customer / GST / Advocate receipts.');
    }
    if (isCustomerRefundMode && !String(unitNumber || '').trim() && !String(unitId || '').trim()) {
      return setSaveError('Select unit number for this refund.');
    }
    if (isCustomerRefundMode && !refundAgainstReceiptIds.length) {
      return setSaveError('Select at least one customer/GST receipt for this refund.');
    }
    if (isCustomerRefundMode && refundSelectedReceiptCapacity <= 0) {
      return setSaveError('Selected receipts do not have refundable balance.');
    }
    if (isCustomerRefundMode) {
      const assignedTotal = refundAllocations.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
      const unassigned = Math.max(0, amt - assignedTotal);
      if (unassigned > 0.009) {
        return setSaveError('Refund amount exceeds selected receipt balance. Select more receipts or reduce amount.');
      }
    }

    if (
      (
        (txnType === 'receipt' &&
          (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')) ||
        isCustomerRefundMode
      ) &&
      !voucherFile &&
      !editingTxn?.voucherUrl
    ) {
      return setSaveError(
        isCustomerRefundMode
          ? 'Chq / Voucher photo is required for Customer Refund.'
          : 'Chq / Voucher photo is required for Customer / GST / Advocate receipts.'
      );
    }
    if (isDummyAdvocateBankSelected && !String(legalChargeType || '').trim()) {
      return setSaveError('Select advocate transaction type (Stamp/Registration/Agreement/Deed).');
    }
    if (txnType === 'receipt' && receiptCategory === 'advocate') {
      const expectedForType = Number(expectedAdvocateAmountForSelection || 0) || 0;
      if (expectedForType <= 0) {
        return setSaveError('Configured amount is not available for selected advocate transaction type.');
      }
      if (amt > advocatePendingAmountForSelection) {
        return setSaveError(
          `Amount exceeds allowed value for selected type. Remaining: ₹${advocatePendingAmountForSelection.toLocaleString('en-IN')}`
        );
      }
    }

    if (txnType === 'receipt' && receiptCategory === 'customer' && selectedLoanToDeveloperAmount > 0 && selectedUnitForReceipt) {
      const selectedTxnId = String(editingTxn?.id || '').trim();
      const isSameUnit = (row: BankTransaction) =>
        (!!unitId && String(row.unitId || '').trim() === String(unitId).trim()) ||
        (!unitId && !!unitNumber && String(row.unitNumber || '').trim() === String(unitNumber).trim());
      const existingTotal = txns
        .filter((row) => String(row.id || '').trim() !== selectedTxnId)
        .filter((row) => String(row.txnType || '').toLowerCase() === 'receipt')
        .filter((row) => String(row.receiptCategory || '').toLowerCase() === 'customer')
        .filter(isSameUnit)
        .filter((row) => (row.isLoanDisbursement === true) === isLoanDisbursement)
        .reduce((sum, row) => sum + Math.max(0, Number(row.amount || 0) || 0), 0);
      const unitAmount = Math.max(0, Number(selectedUnitForReceipt.unitAmount || 0) || 0);
      const allowedAmount = isLoanDisbursement
        ? selectedLoanToDeveloperAmount
        : Math.max(0, unitAmount - selectedLoanToDeveloperAmount);
      if (existingTotal + amt > allowedAmount + 0.009) {
        return setSaveError(
          `${isLoanDisbursement ? 'Loan disbursement' : 'Customer DP'} exceeds the allowed amount of ₹${allowedAmount.toLocaleString('en-IN')}.`
        );
      }
    }

    if (!opts?.forceDuplicateOverride) {
      const matches = detectDuplicateMatchesForCurrentForm({ amount: amt, date: d });
      if (matches.length) {
        setDuplicateMatches(matches);
        setDuplicatePendingSendAfterSave(!!opts?.sendAfterSave);
        setDuplicateDialogOpen(true);
        return;
      }
    }

    setSaving(true);
    try {
      const normalizedUnitId = String(unitId || '').trim();
      const normalizedUnitNumber = String(unitNumber || '').trim();
      const matchedUnitForBooking =
        (normalizedUnitId ? unitOptions.find((u) => String(u.id || '').trim() === normalizedUnitId) : null) ||
        (normalizedUnitNumber
          ? unitOptions.find((u) => String(u.unitNumber || '').trim() === normalizedUnitNumber)
          : null) ||
        null;
      const activeBookingId = String(matchedUnitForBooking?.activeBookingId || '').trim();
      const resolvedUnitId = String(matchedUnitForBooking?.id || normalizedUnitId || '').trim();
      const resolvedUnitNumber = String(matchedUnitForBooking?.unitNumber || normalizedUnitNumber || '').trim();
      const existingBookingId = String((editingTxn as any)?.bookingId || '').trim();
      const existingTxnUnitId = String((editingTxn as any)?.unitId || '').trim();
      const existingTxnUnitNumber = String((editingTxn as any)?.unitNumber || '').trim();
      const isEditingSameUnit =
        !!editingTxn?.id &&
        ((normalizedUnitId && existingTxnUnitId && normalizedUnitId === existingTxnUnitId) ||
          (!normalizedUnitId &&
            normalizedUnitNumber &&
            existingTxnUnitNumber &&
            normalizedUnitNumber === existingTxnUnitNumber));
      const resolvedBookingId =
        normalizedUnitId || normalizedUnitNumber
          ? isEditingSameUnit && existingBookingId
            ? existingBookingId
            : activeBookingId || null
          : null;

      const payload: any = {
        txnType,
        receiptCategory: txnType === 'receipt' ? receiptCategory : null,
        isLoanDisbursement: txnType === 'receipt' && receiptCategory === 'customer' && isLoanDisbursement,
        paymentCategory: txnType === 'payment' ? (paymentCategory ? String(paymentCategory).trim() : null) : null,
        refundAgainstTxnId: isCustomerRefundMode ? (refundAllocations[0]?.txnId || null) : null,
        refundAgainstTxnIds: isCustomerRefundMode
          ? Array.from(new Set(refundAllocations.map((row) => String(row.txnId || '').trim()).filter(Boolean)))
          : null,
        refundAllocations: isCustomerRefundMode
          ? refundAllocations.map((row) => ({
            txnId: String(row.txnId || '').trim(),
            amount: Number(row.amount || 0),
            receiptCategory: row.receiptCategory || 'customer',
          }))
          : null,
        legalChargeType: isDummyAdvocateBankSelected ? legalChargeType : null,
        bankAccountId,
        unitId: resolvedUnitId || null,
        unitNumber: resolvedUnitNumber || null,
        bookingId: resolvedBookingId,
        amount: amt,
        legacyChqDate: chqDateText,
        txnDateYMD,
        partyName: partyName ? String(partyName).trim() : null,
        payerBankName:
          (
            (txnType === 'receipt' && (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')) ||
            (txnType === 'payment' && String(paymentCategory || '').trim().toLowerCase() === 'customer_refund')
          )
            ? (payerBankName ? String(payerBankName).trim() : null)
            : null,
        mode: mode ? String(mode).trim() : null,
        referenceNo: referenceNo ? String(referenceNo).trim() : null,
        notes: notes ? String(notes).trim() : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      const docRef = editingTxn?.id
        ? doc(db, 'projects', currentProjectId, 'bankTransactions', editingTxn.id)
        : doc(collection(db, 'projects', currentProjectId, 'bankTransactions'));

      if (voucherFile) {
        setVoucherUploading(true);
        const safeName = voucherFile.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
        const path = `bank-transactions/${currentProjectId}/${docRef.id}/voucher_${Date.now()}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, voucherFile);
        const url = await getDownloadURL(storageRef);
        payload.voucherUrl = url;
        payload.voucherName = voucherFile.name;
        payload.voucherPath = path;
        payload.voucherUploadedAt = serverTimestamp();
        payload.voucherUploadedBy = currentUser.email;
      }

      if (editingTxn?.id) {
        await updateDoc(docRef, payload);
      } else {
        await setDoc(docRef, {
          ...payload,
          txnDate: null,
          isReconciled: false,
          reconciliationRangeKey: null,
          reconciledAt: null,
          reconciledBy: null,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }

      if (txnType === 'receipt' && receiptCategory === 'customer' && resolvedUnitId && resolvedBookingId) {
        const statusRaw = String(matchedUnitForBooking?.bookingStatus || '').trim().toLowerCase();
        await setDoc(
          doc(db, 'projects', currentProjectId, 'units', resolvedUnitId),
          {
            bookingStatus: statusRaw === 'sold' ? 'sold' : 'booked',
            activeBookingId: resolvedBookingId,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser.email,
          },
          { merge: true }
        );
      }

      const normalizedLegalChargeType: 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee' | null =
        isDummyAdvocateBankSelected && String(legalChargeType || '').trim()
          ? (String(legalChargeType) as 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee')
          : null;
      const savedTxn: BankTransaction = {
        ...(editingTxn || {}),
        id: docRef.id,
        bankAccountId,
        txnType,
        receiptCategory: txnType === 'receipt' ? receiptCategory : null,
        isLoanDisbursement: txnType === 'receipt' && receiptCategory === 'customer' && isLoanDisbursement,
        paymentCategory: txnType === 'payment' ? (paymentCategory ? String(paymentCategory).trim() : null) : null,
        refundAgainstTxnId: isCustomerRefundMode ? (refundAllocations[0]?.txnId || null) : null,
        refundAgainstTxnIds: isCustomerRefundMode
          ? Array.from(new Set(refundAllocations.map((row) => String(row.txnId || '').trim()).filter(Boolean)))
          : null,
        refundAllocations: isCustomerRefundMode
          ? refundAllocations.map((row) => ({
            txnId: String(row.txnId || '').trim(),
            amount: Number(row.amount || 0),
            receiptCategory: row.receiptCategory || 'customer',
          }))
          : null,
        legalChargeType: normalizedLegalChargeType,
        unitId: resolvedUnitId || null,
        unitNumber: resolvedUnitNumber || null,
        bookingId: resolvedBookingId,
        amount: amt,
        txnDate: (editingTxn as any)?.txnDate ?? null,
        legacyChqDate: chqDateText,
        txnDateYMD,
        partyName: partyName ? String(partyName).trim() : null,
        payerBankName:
          (
            (txnType === 'receipt' && (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')) ||
            (txnType === 'payment' && String(paymentCategory || '').trim().toLowerCase() === 'customer_refund')
          )
            ? (payerBankName ? String(payerBankName).trim() : null)
            : null,
        mode: mode ? String(mode).trim() : null,
        referenceNo: referenceNo ? String(referenceNo).trim() : null,
        notes: notes ? String(notes).trim() : null,
        voucherUrl: payload.voucherUrl || editingTxn?.voucherUrl || null,
      };

      setTxnDrawerOpen(false);
      setEditingTxn(null);
      setVoucherFile(null);
      setLockedView(false);
      setEditableFields({
        bank: false,
        amount: false,
        referenceNo: false,
        txnType: false,
        receiptCategory: false,
      });
      setPaymentCategory('');
      setRefundAgainstReceiptIds([]);

      if (opts?.sendAfterSave && String(savedTxn.unitNumber || '').trim()) {
        const paymentTemplate = waTemplates.find((t: any) => String(t?.label || '').trim().toLowerCase() === 'payment receipt confirmation');
        if (!paymentTemplate) {
          setLoadError('Template "Payment Receipt Confirmation" not found.');
          return;
        }
        setWaTemplateId(String(paymentTemplate.id || ''));
        openWhatsAppReceiptDialog(savedTxn);
        setWaSendError('Enter recipient number, then press Send.');
      }
    } catch (err: any) {
      console.error('Save bank transaction failed', err);
      setSaveError(err?.message || 'Failed to save transaction.');
    } finally {
      setSaving(false);
      setVoucherUploading(false);
    }
  };

  const saveTxnAndPrepareSend = async () => {
    await saveTxn({ sendAfterSave: true });
  };

  const confirmDuplicateAndSave = async () => {
    setDuplicateProceeding(true);
    try {
      await saveTxn({
        sendAfterSave: duplicatePendingSendAfterSave,
        forceDuplicateOverride: true,
      });
      setDuplicateDialogOpen(false);
      setDuplicateMatches([]);
      setDuplicatePendingSendAfterSave(false);
    } finally {
      setDuplicateProceeding(false);
    }
  };

  const deleteSelectedTxn = async () => {
    if (!isAdmin) return;
    if (!currentProjectId || !selectedTxn?.id) return;
    const ok = window.confirm('Delete this transaction? This cannot be undone.');
    if (!ok) return;
    setLoadError(null);
    setDeletingTxn(true);
    try {
      await deleteDoc(doc(db, 'projects', currentProjectId, 'bankTransactions', selectedTxn.id));
      setTxnDetailOpen(false);
      setSelectedTxn(null);
      if (editingTxn?.id === selectedTxn.id) {
        setEditingTxn(null);
        setTxnDrawerOpen(false);
      }
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to delete transaction.');
    } finally {
      setDeletingTxn(false);
    }
  };

  const markSelectedTxnReconciledManually = async () => {
    if (!isAdmin) return;
    if (!currentProjectId || !selectedTxn?.id) return;
    if (!currentUser?.email) return;
    setLoadError(null);
    setManualReconciling(true);
    try {
      await updateDoc(doc(db, 'projects', currentProjectId, 'bankTransactions', selectedTxn.id), {
        isReconciled: true,
        reconciliationRangeKey: 'manual',
        reconciledAt: serverTimestamp(),
        reconciledBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setSelectedTxn((prev) => {
        if (!prev) return prev;
        if (prev.id !== selectedTxn.id) return prev;
        return { ...prev, isReconciled: true, reconciliationRangeKey: 'manual' };
      });
    } catch (err: any) {
      console.error('Manual reconcile failed', err);
      setLoadError(err?.message || 'Failed to mark transaction reconciled.');
    } finally {
      setManualReconciling(false);
    }
  };

  const markSelectedTxnUnreconciledManually = async () => {
    if (!isAdmin) return;
    if (!currentProjectId || !selectedTxn?.id) return;
    if (!currentUser?.email) return;
    const ok = window.confirm('Mark this transaction as UNRECONCILED?');
    if (!ok) return;
    setLoadError(null);
    setManualReconciling(true);
    try {
      await updateDoc(doc(db, 'projects', currentProjectId, 'bankTransactions', selectedTxn.id), {
        isReconciled: false,
        reconciliationRangeKey: null,
        reconciledAt: null,
        reconciledBy: null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setSelectedTxn((prev) => {
        if (!prev) return prev;
        if (prev.id !== selectedTxn.id) return prev;
        return {
          ...prev,
          isReconciled: false,
          reconciliationRangeKey: null,
          reconciledAt: null,
          reconciledBy: null,
        } as any;
      });
    } catch (err: any) {
      console.error('Manual unreconcile failed', err);
      setLoadError(err?.message || 'Failed to mark transaction unreconciled.');
    } finally {
      setManualReconciling(false);
    }
  };

  const markTxnTransferProcessed = async (txn: BankTransaction) => {
    if (!isAdmin) return;
    if (!currentProjectId || !txn?.id) return;
    if (!currentUser?.email) return;
    const isCustomerReceipt =
      String(txn.txnType || '').toLowerCase() === 'receipt' &&
      String(txn.receiptCategory || '').toLowerCase() === 'customer';
    if (!isCustomerReceipt) return;
    if ((txn as any)?.fundTransferProcessed === true) return;
    const ok = window.confirm('Confirm transfer request done for this transaction?');
    if (!ok) return;
    setLoadError(null);
    try {
      await updateDoc(doc(db, 'projects', currentProjectId, 'bankTransactions', String(txn.id)), {
        fundTransferProcessed: true,
        fundTransferProcessedManual: true,
        fundTransferProcessedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
    } catch (err: any) {
      console.error('Mark transfer processed failed', err);
      setLoadError(err?.message || 'Failed to mark transfer as done.');
    }
  };

  const refundTopSection = isCustomerRefundMode ? (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        bgcolor: '#f8fafc',
        borderColor: 'rgba(15, 23, 42, 0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        Refund Unit
      </Typography>
      <Stack spacing={1}>
        <Autocomplete
          options={refundUnitOptions}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : String(opt.unitNumber || ''))}
          value={unitId ? refundUnitOptions.find((u) => String(u.id || '') === unitId) || null : null}
          onChange={(_, value) => {
            if (!value || typeof value === 'string') {
              setUnitId('');
              setUnitNumber('');
              setPartyName('');
              setRefundAgainstReceiptIds([]);
              return;
            }
            setUnitId(String(value.id || ''));
            setUnitNumber(String(value.unitNumber || ''));
            setRefundAgainstReceiptIds([]);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Unit Number (Cancelled only)"
              placeholder="Select cancelled unit"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: '#ffffff',
                  borderColor: 'rgba(15, 23, 42, 0.12)',
                },
              }}
            />
          )}
        />
        <TextField
          size="small"
          label="Customer Name"
          value={refundUnitCustomerName || '—'}
          InputProps={{ readOnly: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
              bgcolor: '#ffffff',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            },
          }}
        />
      </Stack>
    </Paper>
  ) : null;

  const refundPostAmountSection = isCustomerRefundMode ? (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        bgcolor: '#f8fafc',
        borderColor: 'rgba(15, 23, 42, 0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        Assign Against Customer Payments <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <Stack spacing={1}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={refundReceiptOptions}
          value={refundReceiptOptions.filter((row) => refundAgainstReceiptIds.includes(row.id))}
          onChange={(_, values) => setRefundAgainstReceiptIds(values.map((row) => String(row.id || '')))}
          getOptionLabel={(row) =>
            `${row.unitNumber} • ₹${formatAmount2(row.amount)} • ${row.dateLabel} • ${row.referenceNo || '—'}`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Checkbox checked={selected} size="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {`${option.unitNumber} • ₹${formatAmount2(option.amount)} • ${option.dateLabel}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ref: {option.referenceNo || '—'} • Refundable: ₹{formatAmount2(option.refundableBalance)}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={option.receiptCategory === 'gst' ? 'GST Payment' : 'Customer Payment'}
                  color={option.receiptCategory === 'gst' ? 'info' : 'success'}
                  variant="outlined"
                />
              </Stack>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((row, index) => (
              <Chip
                {...getTagProps({ index })}
                key={row.id}
                size="small"
                label={`${row.unitNumber} • ₹${formatAmount2(row.amount)} • ${row.receiptCategory === 'gst' ? 'GST' : 'Customer'}`}
                color={row.receiptCategory === 'gst' ? 'info' : 'success'}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select customer/GST receipts"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  borderColor: 'rgba(15, 23, 42, 0.12)',
                },
              }}
            />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          Selected refundable balance: ₹{formatAmount2(refundSelectedReceiptCapacity)}
        </Typography>
        {!Number(String(amount || '').replace(/,/g, '')) ? (
          <Typography variant="caption" color="text.secondary">
            Enter refund amount, then select receipt(s) to assign this refund.
          </Typography>
        ) : null}
        {refundAllocations.length ? (
          <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffffff', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Allocation Preview
            </Typography>
            <Stack spacing={0.4}>
              {refundAllocations.map((row) => {
                const option = refundReceiptOptions.find((x) => x.id === row.txnId);
                if (!option) return null;
                return (
                  <Typography key={row.txnId} variant="caption" color="text.secondary">
                    {option.unitNumber} • {option.referenceNo || '—'} •{' '}
                    {option.receiptCategory === 'gst' ? 'GST Payment' : 'Customer Payment'} • ₹
                    {formatAmount2(row.amount)}
                  </Typography>
                );
              })}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Paper>
  ) : null;

  const loanDisbursementSection = showLoanDisbursementOption ? (
    <FormControlLabel
      control={<Checkbox checked={isLoanDisbursement} onChange={(event) => setIsLoanDisbursement(event.target.checked)} />}
      label={`Is this loan disbursement? (Loan to builder: ₹${selectedLoanToDeveloperAmount.toLocaleString('en-IN')})`}
    />
  ) : null;

  const liveExactDuplicateMatches = liveDuplicateMatches.filter((m) => m.kind === 'exact').slice(0, 3);
  const liveDuplicateAlertSection = liveDuplicateMatches.length ? (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: '#b91c1c',
        color: '#ffffff',
        borderColor: '#7f1d1d',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 800, color: '#ffffff' }}>
        {liveExactDuplicateMatches.length
          ? 'Possible duplicate found before save (exact match).'
          : 'Possible duplicate found before save (likely match within ±10 days).'}
      </Typography>
      {liveExactDuplicateMatches.length ? (
        <Stack spacing={0.5} sx={{ mt: 0.75 }}>
          {liveExactDuplicateMatches.map((match) => (
            <Typography key={`${String(match.row?.id || '')}-${match.kind}`} variant="caption" sx={{ color: '#ffffff' }}>
              {`Date: ${match.dateText} • Unit: ${String(match.row?.unitNumber || '—')} • Party: ${String(
                match.row?.partyName || '—'
              )} • Ref: ${String(match.row?.referenceNo || '—')} • Amount: ₹${Number(match.row?.amount || 0).toLocaleString(
                'en-IN'
              )}`}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Paper>
  ) : null;

  if (!currentProjectId) {
    return <Alert severity="warning">Select a project from the header to view banks.</Alert>;
  }

  return (
    <Box sx={{ pr: { xs: 0, sm: `${Math.max(detailOffset, 0)}px` }, transition: 'padding-right 0.2s ease' }}>
      <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Banks
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'flex', sm: 'none' } }}>
              {isAdmin ? (
                <Button variant="outlined" onClick={openTransferRequestDialog} size="small">
                  Transfer Request
                </Button>
              ) : null}
              <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openAddPayment({ bankAccountId: filterBankId || undefined })} size="small">
                Add
              </Button>
              {isAdmin ? (
                <>
                  <IconButton size="small" onClick={(e) => setBankActionsAnchorEl(e.currentTarget)}>
                    <MoreVertOutlinedIcon />
                  </IconButton>
                  <Menu
                    anchorEl={bankActionsAnchorEl}
                    open={Boolean(bankActionsAnchorEl)}
                    onClose={() => setBankActionsAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                  <MenuItem
                    onClick={() => { setBankActionsAnchorEl(null); openAddPayment({ bankAccountId: filterBankId || undefined, defaultTxnType: 'payment', defaultPaymentCategory: 'customer_refund' }); }}
                  >
                    Add Customer Refund
                  </MenuItem>
                <MenuItem onClick={openTransferHistoryDialog}>
                  Previous Transfer Requests
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setBankActionsAnchorEl(null);
                    importInputRef.current?.click();
                      }}
                    >
                      {importingCsv ? 'Importing…' : 'Import CSV'}
                    </MenuItem>
                  </Menu>
                </>
              ) : null}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <TextField
              size="small"
              placeholder="Search amount, name, unit, ref/chq…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openAddPayment({ bankAccountId: filterBankId || undefined })} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              Add
            </Button>
            {isAdmin ? (
              <Button
                variant="outlined"
                onClick={openTransferRequestDialog}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Transfer Request
              </Button>
            ) : null}
            {isAdmin ? (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setBankActionsAnchorEl(e.currentTarget)}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <MoreVertOutlinedIcon />
                </IconButton>
                <Menu
                  anchorEl={bankActionsAnchorEl}
                  open={Boolean(bankActionsAnchorEl)}
                  onClose={() => setBankActionsAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => { setBankActionsAnchorEl(null); openAddPayment({ bankAccountId: filterBankId || undefined, defaultTxnType: 'payment', defaultPaymentCategory: 'customer_refund' }); }}
                  >
                    Add Customer Refund
                  </MenuItem>
                  <MenuItem onClick={openTransferHistoryDialog}>
                    Previous Transfer Requests
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setBankActionsAnchorEl(null);
                      importInputRef.current?.click();
                    }}
                  >
                    {importingCsv ? 'Importing…' : 'Import CSV'}
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => prepareImportCsvFile(e.target.files?.[0] || null)}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.25, overflowX: 'auto', pb: 0.5 }}>
          {bankHeaderCards.map((card) => (
            <Paper
              key={card.id}
              variant="outlined"
              onClick={() =>
                setFilterBankId((prev) => (String(prev || '') === card.id ? '' : card.id))
              }
              sx={{
                p: 1.25,
                minWidth: 260,
                borderRadius: 1.5,
                borderColor: String(filterBankId || '') === card.id ? 'primary.main' : 'divider',
                background:
                  'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(76,175,80,0.05) 100%)',
                cursor: 'pointer',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {card.name}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
                Current Balance
              </Typography>
              <Typography sx={{ fontSize: 22, lineHeight: 1.1, fontWeight: 900, color: '#0d47a1' }}>
                ₹{Number(card.currentBalance || 0).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                Reconciled: ₹{Number(card.reconciledBalance || 0).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Reconciliation Date: {card.reconciledDate || '—'}
              </Typography>
            </Paper>
          ))}
        </Stack>
        {pendingImport ? (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Import Mapping: {pendingImport.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Map CSV Account values to your system bank accounts before import.
              </Typography>
              {pendingImport.sourceAccounts.map((src) => (
                <Stack key={src} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Typography sx={{ minWidth: { sm: 220 }, fontWeight: 700 }}>{src}</Typography>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <Select
                      value={pendingAccountMapping[src] || ''}
                      onChange={(e) =>
                        setPendingAccountMapping((prev) => ({ ...prev, [src]: String(e.target.value || '') }))
                      }
                    >
                      <MenuItem value="">Select bank account</MenuItem>
                      {bankAccounts.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name || b.bankName || b.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              ))}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setPendingImport(null);
                    setPendingAccountMapping({});
                  }}
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={commitPendingImport} disabled={importingCsv}>
                  {importingCsv ? 'Importing…' : 'Start Import'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : null}
        {importSummary ? (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            {importSummary}
          </Alert>
        ) : null}

        <Stack direction="column" spacing={1} sx={{ mt: 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              rowGap: 1,
              overflowX: { xs: 'auto', sm: 'visible' },
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            <Chip
              label="All"
              clickable
              onClick={() => setFilterType('all')}
              variant={filterType === 'all' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Chip
              label="Receipts"
              color="success"
              clickable
              onClick={() => setFilterType('receipt')}
              variant={filterType === 'receipt' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Chip
              label="Payments"
              color="warning"
              clickable
              onClick={() => setFilterType('payment')}
              variant={filterType === 'payment' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              rowGap: 1,
              overflowX: { xs: 'auto', sm: 'visible' },
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            <Chip
              label="All Recon"
              clickable
              onClick={() => setReconcileFilter('all')}
              variant={reconcileFilter === 'all' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Chip
              label="Reconciled"
              color="success"
              clickable
              onClick={() => setReconcileFilter('reconciled')}
              variant={reconcileFilter === 'reconciled' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Chip
              label={`Unreconciled (${unreconciledCount})`}
              color="error"
              clickable
              onClick={() => setReconcileFilter('unreconciled')}
              variant={reconcileFilter === 'unreconciled' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>
        </Stack>
        {bankAccounts.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            No banks assigned to this project yet. Assign banks in Settings → Projects → Bank Accounts.
          </Alert>
        ) : null}
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {loadError}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading transactions…</Typography>
          </Stack>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'transactionDate'}
                        direction={sortBy === 'transactionDate' ? sortDir : 'desc'}
                        onClick={() => onSortHeader('transactionDate')}
                      >
                        Transaction Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'chqDate'}
                        direction={sortBy === 'chqDate' ? sortDir : 'desc'}
                        onClick={() => onSortHeader('chqDate')}
                      >
                        Chq Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Party</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'amount'}
                        direction={sortBy === 'amount' ? sortDir : 'desc'}
                        onClick={() => onSortHeader('amount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    {showTransferStatusColumn ? <TableCell align="center">TRF</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFiltered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showTransferStatusColumn ? 7 : 6} align="center">
                        No transactions found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((t) => {
                      const d = tsToDate(t.txnDate);
                      const txnDateText = d
                        ? formatDDMMYYYY(d)
                        : String((t as any)?.legacyTransactionDate || '').trim() || '—';
                      const chqDateText = String((t as any)?.legacyChqDate || '').trim() || '—';
                      const bank = t.bankAccountId ? bankById.get(String(t.bankAccountId)) : null;
                      const label = bank?.name || bank?.bankName || (t.bankAccountId ? String(t.bankAccountId) : '—');
                      const amt = typeof t.amount === 'number' ? t.amount : null;
                      const typ = (t.txnType || 'receipt') as TxnType;
                      const isUnreconciled = txnIsUnreconciled(t);
                      return (
                        <TableRow
                          key={t.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isUnreconciled ? 'rgba(245, 158, 11, 0.08)' : undefined,
                            '& td:first-of-type': {
                              borderLeft: isUnreconciled ? '4px solid #f59e0b' : '4px solid transparent',
                            },
                            '&:hover': {
                              bgcolor: isUnreconciled ? 'rgba(245, 158, 11, 0.12)' : undefined,
                            },
                          }}
                          onClick={() => openPaymentDetail(t.id)}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{txnDateText}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{chqDateText}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={typ === 'receipt' ? 'Receipt' : 'Payment'}
                              color={typ === 'receipt' ? 'success' : 'warning'}
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>
                          <TableCell>{label}</TableCell>
                          <TableCell>{t.partyName || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>
                            {amt != null ? amt.toLocaleString('en-IN') : '—'}
                          </TableCell>
                          {showTransferStatusColumn ? (
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              {typ === 'receipt' && String(t.receiptCategory || '').toLowerCase() === 'customer' ? (
                                (t as any)?.fundTransferProcessed === true ? (
                                  <Typography component="span" sx={{ fontSize: 16 }} title="Transfer done">
                                    ✅
                                  </Typography>
                                ) : (
                                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                                    <Typography component="span" sx={{ fontSize: 16 }} title="Transfer pending">
                                      ⏳
                                    </Typography>
                                    {isAdmin ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void markTxnTransferProcessed(t);
                                        }}
                                      >
                                        Mark Done
                                      </Button>
                                    ) : null}
                                  </Stack>
                                )
                              ) : (
                                <Typography component="span" sx={{ color: 'text.disabled' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedFiltered.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const next = Math.max(1, Number(e.target.value || 100));
                setRowsPerPage(next);
                setPage(0);
              }}
              rowsPerPageOptions={[100]}
            />
          </>
        )}
      </Box>

      <Drawer
        anchor="right"
        variant={isMobile ? 'temporary' : 'persistent'}
        open={txnDetailOpen}
        onClose={() => setTxnDetailOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: drawerWidth },
            maxWidth: '100vw',
            top: 'var(--office-appbar-height, 0px)',
            height: 'calc(100vh - var(--office-appbar-height, 0px))',
            borderLeft: '1px solid rgba(0,0,0,0.08)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Transaction Details
            </Typography>
            <Stack direction="row" spacing={1}>
              {selectedTxn &&
              isAdmin &&
              String(selectedTxn.txnType || 'receipt') === 'receipt' &&
              String(selectedTxn.unitNumber || '').trim().length > 0 ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => openWhatsAppReceiptDialog(selectedTxn)}
                  aria-label="Send payment receipt WhatsApp"
                  sx={{ minWidth: 40, px: 1.25 }}
                >
                  <WhatsAppIcon fontSize="small" />
                </Button>
              ) : null}
              {selectedTxn && isAdmin && txnIsUnreconciled(selectedTxn) ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={markSelectedTxnReconciledManually}
                  disabled={manualReconciling}
                  aria-label="Mark reconciled"
                  sx={{ minWidth: 40, px: 1.25 }}
                >
                  <CheckOutlinedIcon fontSize="small" />
                </Button>
              ) : null}
              {selectedTxn && isAdmin && !txnIsUnreconciled(selectedTxn) ? (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={markSelectedTxnUnreconciledManually}
                  disabled={manualReconciling}
                  aria-label="Mark unreconciled"
                  sx={{ minWidth: 40, px: 1.25 }}
                >
                  <UndoOutlinedIcon fontSize="small" />
                </Button>
              ) : null}
              {selectedTxn ? (
                <Button variant="outlined" onClick={() => openDrawerForEdit(selectedTxn)}>
                  Edit
                </Button>
              ) : null}
              {selectedTxn && isAdmin ? (
                <Button
                  color="error"
                  variant="outlined"
                  onClick={deleteSelectedTxn}
                  disabled={deletingTxn}
                >
                  {deletingTxn ? 'Deleting…' : 'Delete'}
                </Button>
              ) : null}
              <Button variant="text" onClick={() => setTxnDetailOpen(false)}>
                ×
              </Button>
            </Stack>
          </Stack>

          {selectedTxn ? (
            <Stack spacing={1}>
              {refundNeedsReceiptLink(selectedTxn) ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => openDrawerForEdit(selectedTxn)}
                    >
                      Link receipt(s)
                    </Button>
                  }
                >
                  This customer refund is not linked to the receipt(s) it refunded. Link it before generating payment details so refunded amounts are excluded.
                </Alert>
              ) : null}
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                  <Chip
                    size="small"
                    label={String(selectedTxn.txnType || 'receipt') === 'receipt' ? 'Receipt' : 'Payment'}
                    color={String(selectedTxn.txnType || 'receipt') === 'receipt' ? 'success' : 'warning'}
                    sx={{ fontWeight: 800 }}
                  />
                  {String(selectedTxn.txnType || '') === 'receipt' && selectedTxn.receiptCategory ? (
                    <Chip size="small" label={String(selectedTxn.receiptCategory).toUpperCase()} variant="outlined" sx={{ fontWeight: 800 }} />
                  ) : null}
                  <Chip
                    size="small"
                    label={txnIsUnreconciled(selectedTxn) ? 'UNRECONCILED' : 'RECONCILED'}
                    color={txnIsUnreconciled(selectedTxn) ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ fontWeight: 800 }}
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Transaction Date: {(() => {
                    const d = tsToDate(selectedTxn.txnDate);
                    if (d) return formatDDMMYYYY(d);
                    const raw = String((selectedTxn as any)?.legacyTransactionDate || '').trim();
                    return raw || '—';
                  })()}
                </Typography>
              </Paper>
              {String((selectedTxn as any)?.legacyChqDate || '').trim() ? (
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Chq Date: {String((selectedTxn as any)?.legacyChqDate || '').trim()}
                  </Typography>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Bank: {(() => {
                    const bank = selectedTxn.bankAccountId ? bankById.get(String(selectedTxn.bankAccountId)) : null;
                    return bank?.name || bank?.bankName || selectedTxn.bankAccountId || '—';
                  })()}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Amount: {typeof selectedTxn.amount === 'number' ? selectedTxn.amount.toLocaleString('en-IN') : '—'}
                </Typography>
              </Paper>

              {selectedTxn.unitNumber ? (
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Unit No: {selectedTxn.unitNumber}
                  </Typography>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Party Name: {selectedTxn.partyName || '—'}
                </Typography>
              </Paper>

              {selectedTxn.payerBankName ? (
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Payer Bank: {selectedTxn.payerBankName}
                  </Typography>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Mode: {selectedTxn.mode || '—'}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Reference No: {selectedTxn.referenceNo || '—'}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Notes: {selectedTxn.notes || '—'}
                </Typography>
              </Paper>

              {selectedTxn.voucherUrl ? (
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Voucher Photo
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(String(selectedTxn.voucherUrl), '_blank', 'noopener,noreferrer')}
                    >
                      View
                    </Button>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Drawer>

      <Dialog
        open={transferDialogOpen}
        onClose={() => {
          if (transferGenerating || transferSending) return;
          setTransferDialogOpen(false);
          setTransferEmailPreviewOpen(false);
          setTransferEmailPreviewError(null);
          setTransferEmailPreviewData(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Transfer Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={transferEligibleCustomerReceipts}
              value={transferSelectedReceiptRows}
              onChange={(_evt, rows) => {
                setTransferSelectedReceiptIds(rows.map((row) => String(row.id)));
              }}
              getOptionLabel={(row) => {
                const date = bankReceiptDisplayDate(row);
                const dateText = date ? formatDDMMYYYY(date) : '—';
                return `${String(row.unitNumber || '—')} • ₹${Number(row.amount || 0).toLocaleString('en-IN')} • ${dateText} • ${String(row.referenceNo || '—')}`;
              }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderOption={(props, option, state) => {
                const date = bankReceiptDisplayDate(option);
                const dateText = date ? formatDDMMYYYY(date) : '—';
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={state.selected}
                      sx={{ mr: 1, p: 0.5 }}
                    />
                    <Typography variant="body2">
                      {`${String(option.unitNumber || '—')} • ₹${Number(option.amount || 0).toLocaleString('en-IN')} • ${dateText} • ${String(option.referenceNo || '—')}`}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer Receipts"
                  placeholder="Search receipt by unit/ref"
                />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                label="Total Selected Customer Receipt"
                value={formatAmount2(transferSelectedReceiptTotal)}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Transfer Request Amount"
                value={transferAmount}
                onChange={(e) => {
                  setTransferAmount(e.target.value);
                }}
                onBlur={() => {
                  const parsed = parseLooseNumber(transferAmount);
                  if (Number.isFinite(parsed) && parsed > 0) {
                    setTransferAmount(formatAmount2(parsed));
                  }
                }}
                helperText={`Suggested (70%): ₹${formatAmount2(transferSelectedReceiptTotal * 0.7)}`}
                fullWidth
              />
              <TextField
                label="Total Customer Receipt Till Date"
                value={formatAmount2(transferCumulativeTillDate)}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Stack>

            {transferGeneratedRequest?.file?.url ? (
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Generated: {String(transferGeneratedRequest?.file?.name || 'Transfer Request.pdf')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(String(transferGeneratedRequest?.file?.url || ''), '_blank', 'noopener,noreferrer')}
                  >
                    Open
                  </Button>
                </Stack>
              </Paper>
            ) : null}

            {transferError ? <Alert severity="error">{transferError}</Alert> : null}
            {transferSuccess ? <Alert severity="success">{transferSuccess}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTransferDialogOpen(false)}
            disabled={transferGenerating || transferSending}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => generateTransferRequestDocument()}
            disabled={transferGenerating}
          >
            {transferGenerating ? 'Generating…' : 'Generate Document'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={openTransferEmailPreview}
            disabled={transferSending || !transferGeneratedRequest?.id}
          >
            {transferSending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferHistoryDialogOpen}
        onClose={() => setTransferHistoryDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Previous Transfer Requests</DialogTitle>
        <DialogContent dividers>
          {transferRequests.length ? (
            <Stack spacing={1.25}>
              {transferRequests.map((request) => {
                const status = String(request?.status || 'generated').toLowerCase();
                const createdAtDate = tsToDate((request as any)?.createdAt);
                const generatedAtDate = tsToDate((request as any)?.generatedAt);
                const statusColor: 'default' | 'info' | 'success' | 'warning' =
                  status === 'emailed'
                    ? 'success'
                    : status === 'generated'
                    ? 'info'
                    : status === 'draft'
                    ? 'warning'
                    : 'default';
                return (
                  <Paper key={String(request.id)} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {String(request?.refNo || '—')}
                        </Typography>
                        <Chip
                          size="small"
                          label={status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Generated'}
                          color={statusColor}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Selected Receipt Total: ₹{Number(request?.selectedReceiptTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Transfer Amount: ₹{Number(request?.transferRequestAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Cumulative Till Date: ₹{Number(request?.projectReceiptCumulativeTillDate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Created: {createdAtDate ? `${formatDDMMYYYY(createdAtDate)} ${createdAtDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : '—'}
                        {' • '}
                        Generated: {generatedAtDate ? `${formatDDMMYYYY(generatedAtDate)} ${generatedAtDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : '—'}
                      </Typography>
                      {String(request?.file?.url || '').trim() ? (
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              window.open(String(request?.file?.url || ''), '_blank', 'noopener,noreferrer')
                            }
                          >
                            Open Document
                          </Button>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Alert severity="info">No transfer requests found yet.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferEmailPreviewOpen}
        onClose={() => {
          if (transferSending) return;
          setTransferEmailPreviewOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm Email</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            {transferEmailPreviewLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            ) : null}
            {transferEmailPreviewError ? <Alert severity="error">{transferEmailPreviewError}</Alert> : null}
            {!transferEmailPreviewLoading && transferEmailPreviewData ? (
              <>
                <TextField
                  label="To"
                  value={transferEmailToDraft}
                  onChange={(e) => setTransferEmailToDraft(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Template"
                  value={String(transferEmailPreviewData.templateName || '')}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Subject"
                  value={String(transferEmailPreviewData.subject || '')}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                {transferEmailPreviewData.attachments?.length ? (
                  <Paper variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Attachments
                    </Typography>
                    <Stack spacing={0.5}>
                      {transferEmailPreviewData.attachments.map((a, idx) => (
                        <Typography key={`${a.key || a.name}-${idx}`} variant="body2">
                          {idx + 1}. {String(a.name || a.key || 'Attachment')}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
                <Paper variant="outlined" sx={{ p: 1.25, maxHeight: 360, overflow: 'auto' }}>
                  <Box
                    sx={{ '& *': { maxWidth: '100%' } }}
                    dangerouslySetInnerHTML={{
                      __html: String(transferEmailPreviewData.bodyHtml || '').trim() || '<em>(No body)</em>',
                    }}
                  />
                </Paper>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTransferEmailPreviewOpen(false)}
            disabled={transferSending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmAndSendTransferEmail}
            disabled={transferSending || transferEmailPreviewLoading || !transferEmailPreviewData}
          >
            {transferSending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={waDialogOpen}
        onClose={() => {
          if (waSending) return;
          setWaDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send Payment Receipt WhatsApp</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            {waTemplatesError ? <Alert severity="error">{waTemplatesError}</Alert> : null}
            <FormControl fullWidth size="small">
              <InputLabel id="wa-bank-template-label">Template</InputLabel>
              <Select
                labelId="wa-bank-template-label"
                label="Template"
                value={waTemplateId}
                onChange={(e) => setWaTemplateId(String(e.target.value || ''))}
              >
                {waTemplates.map((t: any) => (
                  <MenuItem key={String(t.id)} value={String(t.id)}>
                    {String(t.label || t.name || t.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="wa-bank-sender-label">Sender</InputLabel>
              <Select
                labelId="wa-bank-sender-label"
                label="Sender"
                value={waSenderId}
                onChange={(e) => setWaSenderId(String(e.target.value || ''))}
              >
                {waSenders.map((s: any) => (
                  <MenuItem key={String(s.id)} value={String(s.id)}>
                    {String(s.name || s.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="To Number"
              value={waTo}
              onChange={(e) => setWaTo(e.target.value)}
              fullWidth
            />
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                Prefilled from selected transaction
              </Typography>
              <Typography variant="body2">Amount: ₹{Number(selectedTxn?.amount || 0).toLocaleString('en-IN')}</Typography>
              <Typography variant="body2">Date: {(() => {
                const dt = tsToDate(selectedTxn?.txnDate);
                return dt ? formatDDMMYYYY(dt) : '—';
              })()}</Typography>
              <Typography variant="body2">Ref: {String(selectedTxn?.referenceNo || '—')}</Typography>
              <Typography variant="body2">Mode: {String(selectedTxn?.mode || selectedTxn?.receiptCategory || '—')}</Typography>
              <Typography variant="body2">Project: {projectName || '—'}</Typography>
              <Typography variant="body2">Unit: {String(selectedTxn?.unitNumber || '—')}</Typography>
              <Typography variant="body2">Customer: {String(selectedTxn?.partyName || '—')}</Typography>
            </Paper>
            {waTemplatesLoading ? <Typography variant="caption">Loading templates…</Typography> : null}
            {waSendError ? <Alert severity="error">{waSendError}</Alert> : null}
            {waSendSuccess ? <Alert severity="success">{waSendSuccess}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWaDialogOpen(false)}
            disabled={waSending}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={sendPaymentReceiptWhatsApp}
            disabled={waSending}
          >
            {waSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => {
          if (duplicateProceeding) return;
          setDuplicateDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Possible Duplicate Transaction</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Alert severity={duplicateMatches.some((m) => m.kind === 'exact') ? 'error' : 'warning'}>
              {duplicateMatches.some((m) => m.kind === 'exact')
                ? 'Exact duplicate match found.'
                : 'Likely duplicate matches found within ±10 days.'}
            </Alert>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Match</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Party</TableCell>
                    <TableCell>Ref No</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {duplicateMatches.map((m) => (
                    <TableRow key={`${m.row.id}-${m.kind}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={m.kind === 'exact' ? 'error' : 'warning'}
                          label={m.kind === 'exact' ? 'Exact' : `Likely (${m.dayDiff}d)`}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.dateText}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{String((m.row as any)?.unitNumber || '—')}</TableCell>
                      <TableCell>{String((m.row as any)?.partyName || '—')}</TableCell>
                      <TableCell>{String((m.row as any)?.referenceNo || '—')}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        ₹{Number((m.row as any)?.amount || 0).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDuplicateDialogOpen(false);
              setDuplicateMatches([]);
              setDuplicatePendingSendAfterSave(false);
            }}
            disabled={duplicateProceeding}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmDuplicateAndSave}
            disabled={duplicateProceeding}
          >
            {duplicateProceeding ? 'Saving…' : 'Save Anyway'}
          </Button>
        </DialogActions>
      </Dialog>

      <BankTransactionDrawer
        open={txnDrawerOpen}
        onClose={() => {
          setTxnDrawerOpen(false);
          setLockedView(false);
          setEditableFields({
            bank: false,
            amount: false,
            referenceNo: false,
            txnType: false,
            receiptCategory: false,
          });
          setEditingTxn(null);
        }}
        title={
          editingTxn
            ? 'Edit Transaction'
            : txnType === 'payment' && String(paymentCategory || '').trim().toLowerCase() === 'customer_refund'
              ? 'Add Customer Refund'
              : 'Add Transaction'
        }
        saveError={saveError}
        saving={saving}
        voucherUploading={voucherUploading}
        onSave={saveTxn}
        onSaveAndSend={saveTxnAndPrepareSend}
        showSaveAndSendButton={txnType === 'receipt' && !!String(unitNumber || '').trim()}
        saveAndSendLabel="Save & Send"
        customTopSection={refundTopSection}
        customPostAmountSection={
          refundPostAmountSection || loanDisbursementSection ? (
            <Stack spacing={1}>{refundPostAmountSection}{loanDisbursementSection}</Stack>
          ) : null
        }
        customAfterAmountSection={liveDuplicateAlertSection}
        txnType={txnType}
        setTxnType={setTxnType}
        receiptCategory={receiptCategory}
        setReceiptCategory={setReceiptCategory}
        legalChargeType={legalChargeType}
        setLegalChargeType={setLegalChargeType}
        showLegalChargeType={isDummyAdvocateBankSelected}
        eligibleBankAccounts={eligibleBankAccounts}
        bankAccountId={bankAccountId}
        setBankAccountId={setBankAccountId}
        txnDateYMD={txnDateYMD}
        setTxnDateYMD={setTxnDateYMD}
        amount={amount}
        setAmount={setAmount}
        showUnitPicker={
          !isCustomerRefundMode &&
          txnType === 'receipt' &&
          (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')
        }
        eligibleUnitOptions={eligibleUnitOptions}
        unitId={unitId}
        setUnitId={setUnitId}
        unitNumber={unitNumber}
        setUnitNumber={setUnitNumber}
        pendingAmountText={
          txnType === 'receipt' && (receiptCategory === 'customer' || receiptCategory === 'gst') && (unitId || unitNumber)
            ? `${receiptCategory === 'gst' ? 'GST Pending: ' : 'Pending: '}${(() => {
                const expected =
                  unitId && eligibleUnitOptions.find((u) => u.id === unitId)?.unitAmount != null
                    ? eligibleUnitOptions.find((u) => u.id === unitId)?.unitAmount
                    : unitNumber
                      ? unitOptions.find((u) => u.unitNumber === unitNumber)?.unitAmount
                      : null;
                if (expected == null) return '—';
                if (receiptCategory === 'gst') {
                  const selectedUnitForGst =
                    (unitId ? unitOptions.find((u) => u.id === unitId) : null) ||
                    (unitNumber ? unitOptions.find((u) => u.unitNumber === unitNumber) : null) ||
                    null;
                  const gstExpected = computeGstAmountForUnit(
                    expected,
                    selectedUnitForGst as any,
                    {
                      gstPercentByUnitType: projectGstPercentByUnitType,
                      gstDefaultPercent: projectDefaultGstPercent,
                    },
                    1
                  );
                  const gstPaid =
                    (unitId ? gstReceiptTotals.byUnitId.get(unitId) : 0) ||
                    (unitNumber ? gstReceiptTotals.byUnitNumber.get(unitNumber) : 0) ||
                    0;
                  const pending = Math.max(0, gstExpected - gstPaid);
                  return pending.toLocaleString('en-IN');
                }
                const paid =
                  (unitId ? customerReceiptTotals.byUnitId.get(unitId) : 0) ||
                  (unitNumber ? customerReceiptTotals.byUnitNumber.get(unitNumber) : 0) ||
                  0;
                const pending = Math.max(0, expected - paid);
                return pending.toLocaleString('en-IN');
              })()}`
            : txnType === 'receipt' && receiptCategory === 'advocate' && (unitId || unitNumber)
            ? `Advocate Pending: ₹${advocatePendingAmountForSelection.toLocaleString('en-IN')}`
            : null
        }
        partyName={partyName}
        setPartyName={setPartyName}
        partyNameOptions={partyNameOptions}
        payerBankName={payerBankName}
        setPayerBankName={setPayerBankName}
        payerBankNameOptions={payerBankNameOptions}
        onPayerBankClear={() => setPayerBankAutofillSuppressed(true)}
        paymentModeOptions={paymentModeOptions}
        mode={mode}
        setMode={setMode}
        referenceNo={referenceNo}
        setReferenceNo={setReferenceNo}
        notes={notes}
        setNotes={setNotes}
        voucherInputRef={voucherInputRef}
        voucherFile={voucherFile}
        setVoucherFile={setVoucherFile}
        voucherRequired={
          (txnType === 'receipt' &&
            (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate')) ||
          isCustomerRefundMode
        }
        existingVoucherUrl={editingTxn?.voucherUrl || null}
        drawerWidth={drawerWidth}
        readOnly={lockedView}
        editableFields={editableFields}
        onToggleEditField={(field) =>
          setEditableFields((prev) => {
            if (field === 'bank') return { ...prev, bank: !prev.bank };
            if (field === 'amount') return { ...prev, amount: !prev.amount };
            if (field === 'referenceNo') return { ...prev, referenceNo: !prev.referenceNo };
            if (field === 'txnType') {
              const next = !prev.txnType;
              return { ...prev, txnType: next, receiptCategory: next };
            }
            return { ...prev, receiptCategory: !prev.receiptCategory };
          })
        }
      />
    </Box>
  );
}
